/** Supported UI locales */
export type Locale = 'ru' | 'en';

/** Default locale — Russian */
export const DEFAULT_LOCALE: Locale = 'ru';

/** All supported locales */
export const SUPPORTED_LOCALES: readonly Locale[] = ['ru', 'en'] as const;

/** localStorage key for persisted locale */
export const LOCALE_STORAGE_KEY = 'roll1-locale';

/**
 * Flat translation dictionary. Keys use dot-notation grouping.
 * All values are strings.
 */
export interface TranslationKeys {
  'search.group.text': string;
  'ai.tabReady': string;
  'walls.draw': string;
  'walls.connect': string;
  'walls.connectHint': string;
  'walls.drawHint': string;
  'walls.connectChanged': string;
  'editor.format': string;
  'editor.bold': string;
  'editor.italic': string;
  'editor.heading': string;
  'editor.list': string;
  'editor.quote': string;
  'editor.undo': string;
  'editor.redo': string;
  'editor.orderedList': string;
  'editor.link': string;
  'editor.linkTitle': string;
  'editor.linkLabel': string;
  'editor.linkInvalid': string;
  'studio.maps.besideLong': string;
  'studio.maps.addTitle': string;
  'studio.maps.addCampaignMap': string;
  'studio.note.open': string;
  'beat.next.openBeat': string;
  'aiField.languages': string;
  'aiField.backstory': string;
  'aiField.roleplay_json': string;
  'aiField.playersText': string;
  'aiField.gmText': string;
  'aiField.title': string;
  'aiField.text': string;
  'aiField.completed': string;
  'ai.results.history': string;
  'ai.results.historyHint': string;
  'ai.results.empty': string;
  'ai.results.saved': string;
  'ai.results.noChanges': string;
  'ai.results.partial': string;
  'ai.results.kept': string;
  'ai.results.details': string;
  'ai.results.open': string;
  'ai.results.menu': string;
  'ai.results.hide': string;
  'ai.results.restore': string;
  'ai.results.hidden': string;
  'ai.results.undo': string;
  'ai.results.undoTitle': string;
  'ai.results.undoBody': string;
  'ai.results.review': string;
  'ai.results.pending': string;
  'ai.results.reviewHint': string;
  'ai.results.apply': string;
  'ai.results.decline': string;
  'ai.results.declined': string;
  'ai.results.declineError': string;
  'ai.results.applying': string;
  'ai.results.dismissing': string;
  'ai.results.selected': string;
  'ai.results.proposed': string;
  'ai.results.mode': string;
  'ai.results.suggest': string;
  'ai.results.auto': string;
  'ai.results.modeHint': string;
  'ai.results.dependency': string;
  // === Toolbar ===
  'toolbar.select': string;
  'toolbar.draw': string;
  'toolbar.erase': string;
  'toolbar.shape': string;
  'toolbar.measure': string;
  'toolbar.ping': string;
  'toolbar.pingHint': string;
  'toolbar.fog': string;
  'toolbar.note': string;
  'toolbar.portal': string;
  'toolbar.selectDrawing': string;
  'toolbar.text': string;
  'toolbar.aoe': string;
  'toolbar.mapObject': string;
  'toolbar.wall': string;
  'toolbar.token': string;
  'toolbar.token.helper': string;
  'toolbar.undo': string;
  'toolbar.redo': string;

  // === Toolbar Groups ===
  'toolbar.group.select': string;
  'toolbar.group.drawing': string;
  'toolbar.group.fog': string;
  'toolbar.group.measure': string;
  'toolbar.group.pins': string;
  'toolbar.group.text': string;
  'toolbar.group.token': string;
  'toolbar.group.mapObjects': string;
  'toolbar.group.note': string;
  'toolbar.group.objects': string;
  'toolbar.group.aiEdit': string;
  'toolbar.inpaintRect': string;
  'toolbar.inpaintLasso': string;
  'toolbar.detailRect': string;
  'toolbar.initiative': string;
  'toolbar.held': string;
  'toolbar.snapActive': string;
  'toolbar.overflow': string;

  // === Canvas Context Menu ===
  'contextMenu.addToken': string;
  'contextMenu.addPin': string;
  'contextMenu.actAs': string;
  'contextMenu.addText': string;
  'contextMenu.editToken': string;
  'contextMenu.deleteToken': string;
  'contextMenu.copyToken': string;
  'contextMenu.moveToFront': string;
  'contextMenu.moveToBack': string;
  'contextMenu.openEntry': string;
  'contextMenu.movePin': string;
  'contextMenu.deletePin': string;
  'contextMenu.deleteDrawing': string;
  'contextMenu.changeColor': string;
  'contextMenu.viewToken': string;

  // === Drawing Selection ===
  'drawingSelection.stroke': string;
  'drawingSelection.strokes': string;
  'drawingSelection.toolbar': string;
  'drawingSelection.delete': string;

  // === Sidebar Tabs ===
  'sidebar.map': string;
  'sidebar.journal': string;
  'sidebar.notes': string;
  'sidebar.chat': string;
  'sidebar.srd': string;
  'sidebar.initiative': string;
  'sidebar.participants': string;
  'sidebar.ai': string;
  'sidebar.characters': string;

  // === Sidebar Sections (Map tab) ===
  'sidebar.section.pages': string;
  'sidebar.section.tokens': string;
  'sidebar.section.pins': string;
  'sidebar.section.mapObjects': string;

  // === Inspector ===
  'inspector.tokenVision': string;
  'inspector.visionRange': string;
  'inspector.darkvision': string;
  'inspector.pinOpenEntry': string;

  // === Common Actions ===
  'common.save': string;
  'common.cancel': string;
  'common.delete': string;
  'common.create': string;
  'common.edit': string;
  'common.back': string;
  'common.close': string;
  'common.loading': string;
  'common.search': string;
  'common.reset': string;
  'common.next': string;
  'common.confirm': string;
  'common.name': string;
  'common.none': string;

  // === Auth ===
  'auth.login': string;
  'auth.loginTitle': string;
  'auth.loginBusy': string;
  'auth.register': string;
  'auth.registerTitle': string;
  'auth.registerBusy': string;
  'auth.registerSubmit': string;
  'auth.inviteCode': string;
  'auth.inviteCodeHint': string;
  'auth.inviteCodePlaceholder': string;
  'auth.email': string;
  'auth.password': string;
  'auth.passwordHint': string;
  'auth.displayName': string;
  'auth.hasAccount': string;
  'auth.noAccount': string;
  'auth.showPassword': string;
  'auth.hidePassword': string;
  'auth.profileTitle': string;
  'auth.displayNameLabel': string;
  'auth.saveBusy': string;

  // === Lobby ===
  'lobby.createSession': string;
  'lobby.joinSession': string;
  'lobby.yourName': string;
  'lobby.sessionCode': string;
  'lobby.connecting': string;
  'lobby.enter': string;

  // === Session List ===
  'sessionList.title': string;
  'sessionList.session': string;
  'sessionList.player': string;
  'sessionList.loading': string;
  'sessionList.copied': string;
  'sessionList.copyCode': string;
  'sessionList.today': string;
  'sessionList.yesterday': string;
  'sessionList.daysAgo': string;
  'sessionList.rename': string;
  'sessionList.delete': string;
  'sessionList.copyInvite': string;
  'sessionList.deleteConfirm': string;
  'sessionList.deleteConfirmTitle': string;
  'sessionList.previewComingSoon': string;
  'sessionList.inviteCopied': string;

  // === Chat ===
  'chat.title': string;
  'chat.placeholder': string;
  'chat.rolls': string;
  'chat.helpUsage': string;
  'chat.nameChanged': string;
  'chat.helpRoll': string;
  'chat.helpName': string;
  'chat.helpClear': string;
  'chat.helpGm': string;
  'chat.unknownCommand': string;
  'chat.welcome': string;
  'chat.welcomeShortcuts': string;

  // === Initiative ===
  'initiative.title': string;
  'initiative.next': string;
  'initiative.otherPage': string;
  'initiative.add': string;
  'initiative.addMenu': string;
  'initiative.more': string;
  'initiative.closeHint': string;
  'initiative.collapse': string;
  'initiative.noFight': string;
  'initiative.endedRounds': string;
  'initiative.collect': string;
  'initiative.sameAgain': string;
  'initiative.orFromMap': string;
  'initiative.whoInFight': string;
  'initiative.nOfOnMap': string;
  'initiative.onlySelected': string;
  'initiative.party': string;
  'initiative.foes': string;
  'initiative.hiddenFromPlayers': string;
  'initiative.rollsHimself': string;
  'initiative.dexMod': string;
  'initiative.noSheet': string;
  'initiative.playersRoll': string;
  'initiative.groupSame': string;
  'initiative.groupSameTip': string;
  'initiative.startFight': string;
  'initiative.d20dex': string;
  'initiative.d20dexTip': string;
  'initiative.cancel': string;
  'initiative.assembled': string;
  'initiative.waitingRolls': string;
  'initiative.beginNow': string;
  'initiative.beginNowTip': string;
  'initiative.roundN': string;
  'initiative.waiting': string;
  'initiative.yourTurn': string;
  'initiative.youNext': string;
  'initiative.youAfter': string;
  'initiative.youDelayed': string;
  'initiative.nextTip': string;
  'initiative.nextTurn': string;
  'initiative.prev': string;
  'initiative.delayMine': string;
  'initiative.delayMineTip': string;
  'initiative.endTurn': string;
  'initiative.rollForPlayer': string;
  'initiative.rollForPlayerName': string;
  'initiative.notRolled': string;
  'initiative.roll': string;
  'initiative.editValue': string;
  'initiative.valueOf': string;
  'initiative.hpOf': string;
  'initiative.hpWord': string;
  'initiative.hpTip': string;
  'initiative.markOut': string;
  'initiative.zeroHp': string;
  'initiative.hiddenTip': string;
  'initiative.hidden': string;
  'initiative.tieTip': string;
  'initiative.tie': string;
  'initiative.outTip': string;
  'initiative.downTip': string;
  'initiative.unconscious': string;
  'initiative.fromRound': string;
  'initiative.fromRoundTip': string;
  'initiative.owed': string;
  'initiative.owedTip': string;
  'initiative.yourTurnTag': string;
  'initiative.actsTag': string;
  'initiative.showOnMap': string;
  'initiative.showOnMapName': string;
  'initiative.openSheet': string;
  'initiative.hpMenu': string;
  'initiative.enterValue': string;
  'initiative.enterGroupValue': string;
  'initiative.reroll': string;
  'initiative.delayTurn': string;
  'initiative.split': string;
  'initiative.showToPlayers': string;
  'initiative.hideFromPlayers': string;
  'initiative.removeFromFight': string;
  'initiative.removeGroup': string;
  'initiative.groupNote': string;
  'initiative.ownValue': string;
  'initiative.showEach': string;
  'initiative.collapseGroup': string;
  'initiative.moreOf': string;
  'initiative.drag': string;
  'initiative.delayedHeader': string;
  'initiative.untilEndOfRound': string;
  'initiative.untilEndTip': string;
  'initiative.join': string;
  'initiative.joinTip': string;
  'initiative.joinTipHidden': string;
  'initiative.waitsDelayed': string;
  'initiative.endFight': string;
  'initiative.endAsk': string;
  'initiative.endAskHint': string;
  'initiative.endConfirm': string;
  'initiative.menuNote': string;
  'initiative.onMap': string;
  'initiative.appearedAfter': string;
  'initiative.toFight': string;
  'initiative.dismissNew': string;
  'initiative.pickAll': string;
  'initiative.new': string;
  'initiative.joinGroups': string;
  'initiative.rollAndAdd': string;
  'initiative.turnStays': string;
  'initiative.close': string;
  'initiative.undo': string;
  'initiative.n.started': string;
  'initiative.n.startedAll': string;
  'initiative.n.begun': string;
  'initiative.n.addedGroup': string;
  'initiative.n.addedLater': string;
  'initiative.n.addedNow': string;
  'initiative.n.removedOne': string;
  'initiative.n.removedMany': string;
  'initiative.n.value': string;
  'initiative.n.roll': string;
  'initiative.n.reroll': string;
  'initiative.n.move': string;
  'initiative.n.top': string;
  'initiative.n.keeps': string;
  'initiative.n.owed': string;
  'initiative.n.acted': string;
  'initiative.n.same': string;
  'initiative.n.allRolled': string;
  'initiative.n.round': string;
  'initiative.n.lost': string;
  'initiative.n.delayed': string;
  'initiative.n.joined': string;
  'initiative.n.split': string;
  'initiative.n.splitOwed': string;
  'initiative.n.ended': string;
  'initiative.n.restored': string;
  'initiative.n.firstTurn': string;
  'initiative.n.back': string;
  'initiative.n.turnEnded': string;
  'contextMenu.initiativeAdd': string;
  'contextMenu.initiativeAddN': string;
  'contextMenu.initiativeRemove': string;
  'contextMenu.initiativeRemoveN': string;
  'contextMenu.alreadyInFight': string;

  // === Tokens ===
  'tokens.title': string;
  'tokens.add': string;
  'tokens.boundToSheet': string;
  'tokens.sheetLabel': string;
  'tokens.tokenName': string;
  'tokens.defaultName': string;
  'tokens.added': string;
  'tokens.created': string;

  // === Dice ===
  'dice.title': string;
  'dice.roll': string;

  // === Pages ===
  'pages.deleteConfirmTitle': string;
  'pages.deleteConfirmDesc': string;
  'pages.deleteConfirmDescWithChildren': string;
  'pages.deletePlayersHere': string;
  'pages.deleteChooseTarget': string;

  // === Characters ===
  'characters.title': string;
  'characters.createWizard': string;
  'characters.empty': string;
  'characters.create': string;
  'characters.createBlank': string;
  'characters.newCharacter': string;
  'characters.nameLabel': string;
  'characters.namePlaceholder': string;
  'characters.templateLabel': string;
  'characters.typeLabel': string;
  'characters.typePC': string;
  'characters.typeNPC': string;
  'characters.builtIn': string;
  'characters.foreignSheet': string;
  'characters.performanceWarning': string;
  'characters.assignOwner': string;
  'characters.assignOwnerDescription': string;
  'characters.publicFields': string;
  'characters.sheetVisibleTitle': string;
  'characters.sheetHiddenTitle': string;
  'characters.cardVisibilityTitle': string;
  'characters.publicFieldsDescription': string;
  'characters.noOwner': string;
  'characters.createWithoutOwner': string;
  'characters.createAndAssign': string;
  'characters.ungrouped': string;
  'characters.newGroup': string;
  'characters.groupName': string;
  'characters.deleteGroup': string;
  'characters.deleteConfirmTitle': string;
  'characters.deleteConfirmDesc': string;
  'characters.renameGroup': string;

  // === Participants ===
  'participants.title': string;

  // === Notes ===
  'notes.title': string;
  'notes.empty': string;
  'notes.untitled': string;
  'notes.gmOnly': string;
  'notes.newNote': string;
  'notes.editNote': string;
  'notes.noteTitle': string;
  'notes.noteContent': string;
  'notes.icon': string;
  'notes.color': string;
  'notes.selectColor': string;

  // === Note icons ===
  'noteIcon.pin': string;
  'noteIcon.scroll': string;
  'noteIcon.skull': string;
  'noteIcon.chest': string;
  'noteIcon.star': string;
  'noteIcon.question': string;
  'noteIcon.exclamation': string;
  'noteIcon.camp': string;
  'noteIcon.sword': string;
  'noteIcon.key': string;

  // === Reconnect ===
  'reconnect.message': string;

  // === Connection Status ===
  'status.online': string;
  'status.reconnecting': string;

  // === Workspace Presets ===
  'preset.explore': string;
  'preset.combat': string;
  'preset.prep': string;
  'preset.fullscreen': string;
  'preset.exit': string;

  // === Shortcuts ===
  'shortcuts.title': string;
  'shortcuts.hint': string;
  'shortcuts.sectionTools': string;
  'shortcuts.sectionCamera': string;
  'shortcuts.sectionTokens': string;
  'shortcuts.sectionChat': string;
  'shortcuts.sectionWorkspace': string;
  'shortcuts.toolSelect': string;
  'shortcuts.toolDraw': string;
  'shortcuts.toolEraser': string;
  'shortcuts.toolRuler': string;
  'shortcuts.toolPing': string;
  'shortcuts.toolFog': string;
  'shortcuts.toolNote': string;
  'shortcuts.toolPortal': string;
  'shortcuts.snapToggle': string;
  'shortcuts.zoomIn': string;
  'shortcuts.zoomOut': string;
  'shortcuts.fitToScreen': string;
  'shortcuts.exitFullscreen': string;
  'shortcuts.deleteTokens': string;
  'shortcuts.quickSelectToken': string;
  'shortcuts.nextTurn': string;
  'shortcuts.openMapPanel': string;
  'shortcuts.presetExplore': string;
  'shortcuts.presetCombat': string;
  'shortcuts.presetPrep': string;
  'shortcuts.toggleSidebar': string;
  'shortcuts.showShortcuts': string;
  'shortcuts.saveSession': string;
  'shortcuts.loadSession': string;
  'shortcuts.undo': string;
  'shortcuts.redo': string;
  'shortcuts.toolSelectDrawing': string;
  'shortcuts.toolText': string;
  'shortcuts.toolAoe': string;
  'shortcuts.toolMapObject': string;
  'shortcuts.fogBrushDec': string;
  'shortcuts.fogBrushInc': string;
  'shortcuts.copy': string;
  'shortcuts.paste': string;
  'shortcuts.toggleInitiative': string;

  // === Tool Options ===
  'toolOptions.snapToGrid': string;
  'toolOptions.color': string;
  'toolOptions.width': string;
  'toolOptions.mode': string;
  'toolOptions.modeSimple': string;
  'toolOptions.modeAlternating': string;
  'toolOptions.modeEuclidean': string;
  'toolOptions.modeHex': string;
  'toolOptions.gridSizeFt': string;
  'toolOptions.shape': string;
  'toolOptions.shapeRect': string;
  'toolOptions.shapePolygon': string;
  'toolOptions.shapeCircle': string;
  'toolOptions.navigation': string;
  'toolOptions.navFree': string;
  'toolOptions.navConfirm': string;
  'toolOptions.navGmOnly': string;
  'toolOptions.reFog': string;
  'toolOptions.dynamicFogHint': string;
  'toolOptions.clearAllFog': string;
  'toolOptions.coverAllFog': string;
  'toolOptions.shapeLine': string;
  'toolOptions.fogMode': string;
  'toolOptions.brushSize': string;
  'toolOptions.mapSettings': string;
  'toolOptions.gridSize': string;
  'toolOptions.mapScale': string;
  'toolOptions.bgColor': string;
  'toolOptions.deleteMap': string;
  'toolOptions.confirmDeleteMap': string;
  'toolOptions.fogSystem': string;
  'toolOptions.fogManual': string;
  'toolOptions.fogDynamic': string;
  'toolOptions.wallType': string;
  'toolOptions.wallTypeWall': string;
  'toolOptions.wallTypeDoor': string;
  'toolOptions.wallTypeWindow': string;
  'toolOptions.wallTypeTerrain': string;
  'toolOptions.wallTypeSecret': string;
  'toolOptions.wallTypeEthereal': string;
  'toolOptions.aiEditMode': string;
  'toolOptions.sketchToMap': string;

  // === Token Edit Dialog ===
  'tokenEdit.title': string;
  'tokenEdit.name': string;
  'tokenEdit.size': string;
  'tokenEdit.sizeTiny': string;
  'tokenEdit.sizeMedium': string;
  'tokenEdit.sizeLarge': string;
  'tokenEdit.sizeHuge': string;
  'tokenEdit.sizeGargantuan': string;
  'tokenEdit.color': string;
  'tokenEdit.notes': string;
  'tokenEdit.visible': string;
  'tokenEdit.light': string;
  'tokenEdit.lightEnabled': string;
  'tokenEdit.lightBright': string;
  'tokenEdit.lightDim': string;
  'tokenEdit.lightColor': string;
  'tokenEdit.visionType': string;
  'tokenEdit.visionTypeNormal': string;
  'tokenEdit.visionTypeDarkvision': string;

  // === Global Illumination ===
  'page.globalIllumination': string;
  'page.illuminationBright': string;
  'page.illuminationDim': string;
  'page.illuminationDark': string;
  'page.illuminationColor': string;

  // === Conditions Dialog ===
  'conditions.title': string;

  // === Context Menu ===
  'contextMenu.inspect': string;
  'contextMenu.edit': string;
  'contextMenu.conditions': string;
  'contextMenu.hp': string;
  'contextMenu.resize': string;
  'contextMenu.addAura': string;
  'contextMenu.layerUp': string;
  'contextMenu.layerDown': string;
  'contextMenu.copy': string;
  'contextMenu.delete': string;
  'contextMenu.auras': string;
  'contextMenu.fogReset': string;
  'contextMenu.hideToken': string;
  'contextMenu.showToken': string;
  'contextMenu.enableVision': string;
  'contextMenu.disableVision': string;
  'contextMenu.openSheet': string;

  // === Link Context Menu ===
  'linkMenu.edit': string;
  'linkMenu.navigate': string;
  'linkMenu.toggleVisibility': string;
  'linkMenu.delete': string;
  'linkMenu.zone': string;

  // === Aura Editor ===
  'aura.editTitle': string;
  'aura.addTitle': string;
  'aura.radiusFt': string;
  'aura.color': string;
  'aura.opacity': string;
  'aura.label': string;
  'aura.style': string;
  'aura.styleSolid': string;
  'aura.styleDashed': string;
  'aura.stylePulse': string;
  'aura.gmOnly': string;

  // === Location Link Dialog ===
  'locationLink.editTitle': string;
  'locationLink.createTitle': string;
  'locationLink.targetPage': string;
  'locationLink.selectPage': string;
  'locationLink.label': string;
  'locationLink.labelPlaceholder': string;
  'locationLink.style': string;
  'locationLink.stylePortal': string;
  'locationLink.styleDoor': string;
  'locationLink.styleStairs': string;
  'locationLink.styleArea': string;

  // === Template Editor ===
  'templates.title': string;
  'templates.builtIn': string;
  'templates.createTemplate': string;
  'templates.editTemplate': string;
  'templates.newTemplate': string;
  'templates.nameLabel': string;
  'templates.newSection': string;
  'templates.newField': string;
  'templates.addField': string;
  'templates.addSection': string;
  'templates.deleteSection': string;

  // === AI Chat ===
  'ai.title': string;
  'ai.newChat': string;
  'aiCard.was': string;
  'aiCard.now': string;
  'ai.historyLoadError': string;
  'ai.historyRenameError': string;
  'ai.historyBusy': string;
  'ai.historyRetention': string;
  'ai.historyTitle': string;
  'ai.historySave': string;
  'ai.historyCancel': string;
  'ai.historyCurrent': string;
  'ai.historyRename': string;
  'ai.historyMore': string;
  'ai.historyRetry': string;
  'ai.emptyHint': string;
  'ai.thinking': string;
  'ai.historySummarized': string;
  'ai.placeholder': string;
  'ai.textModel': string;
  'ai.models': string;
  'ai.imageModel': string;
  'aiModel.mapModel': string;
  'settings.aiTextModel': string;
  'settings.aiTextModelServer': string;
  'aiCard.changes': string;
  'aiChange.beatMoved': string;
  'ai.plan.step.move': string;
  'aiGroup.sheet': string;
  'aiGroup.beat': string;
  'aiGroup.journal': string;
  'aiGroup.token': string;
  'aiGroup.page': string;
  'aiGroup.other': string;
  'aiGroup.count.one': string;
  'aiGroup.count.few': string;
  'aiGroup.count.many': string;
  'aiGroup.show': string;
  'aiGroup.hide': string;
  'aiGroup.empty': string;
  'aiGroup.yes': string;
  'aiGroup.no': string;
  'aiField.item': string;
  'aiField.equipped': string;
  'aiField.beatOrder': string;
  'aiField.proficiencies': string;
  'aiField.proficiency_cats': string;
  'aiField.subclass': string;
  'aiField.subrace': string;
  'aiField.sessionLabel': string;
  'aiField.status': string;
  'aiField.name': string;
  'aiCard.undone': string;
  'aiCard.undoAll': string;
  'aiCard.undo': string;
  'aiCard.undoRelated': string;
  'aiCard.kept': string;
  'aiCard.toastUndone': string;
  'aiCard.undoRefused': string;
  'aiChange.beatCreated': string;
  'aiChange.reserveCreated': string;
  'aiChange.outcomeCreated': string;
  'aiChange.noteCreated': string;
  'aiChange.questCreated': string;
  'aiChange.entryCreated': string;
  'aiChange.entryChanged': string;
  'aiChange.prepExtended': string;
  'aiChange.objectiveDone': string;
  'aiChange.objectiveUndone': string;
  'aiChange.beatStatus': string;
  'aiChange.beatRenamed': string;
  'aiChange.beatChanged': string;
  'aiChange.currentBeat': string;
  'aiChange.timelineDraft': string;
  'aiChange.pageCreated': string;
  'aiChange.pinCreated': string;
  'aiChange.tokenCreated': string;
  'aiChange.sheetCreated': string;
  'aiChange.deleted': string;
  'aiUndo.conflictTitle': string;
  'aiUndo.conflictBodyOne': string;
  'aiUndo.conflictBodyMany': string;
  'aiUndo.restore': string;
  'aiUndo.keepRest': string;
  'aiUndo.keepOnly': string;
  'aiUndo.cancel': string;
  'aiMark.added': string;
  'aiMark.changed': string;
  'pinPopup.confirmBodyWhole': string;
  'aiCard.irreversible': string;
  'aiCard.field.description': string;
  'aiCard.field.content': string;
  'aiCard.field.gmNotes': string;
  'aiCard.field.rewards': string;
  'aiChange.tokenChanged': string;
  'aiChange.tokenMoved': string;
  'aiChange.sheetEdited': string;
  'aiChange.pageRenamed': string;
  'aiChange.fogChanged': string;
  'aiChange.timeMoved': string;
  'aiChange.linkAdded': string;
  'aiChange.tagsChanged': string;
  'ai.thinkingLabel': string;
  'ai.stop': string;
  'ai.conversationHistory': string;
  'ai.noConversations': string;
  'ai.loadingHistory': string;
  'ai.toolErrorFriendly': string;
  'ai.stoppedByGM': string;
  'ai.openThinkingModal': string;
  'ai.emptyTiaGreeting': string;
  'ai.emptyPersonaGreeting': string;
  'ai.personaMemory': string;
  'ai.chronicle': string;
  'ai.generating': string;
  'ai.saveAsJournal': string;
  'ai.noPersonaMemory': string;
  'ai.requestChronicle': string;
  'ai.requestEpilogue': string;

  // === AI Tool Labels ===
  'aiTool.getSessionInfo': string;
  'aiTool.getCurrentPage': string;
  'aiTool.listSheets': string;
  'aiTool.getSheet': string;
  'aiTool.createNote': string;
  'aiTool.searchMonsters': string;
  'aiTool.getMonster': string;
  'aiTool.searchClasses': string;
  'aiTool.searchSpells': string;
  'aiTool.createSheet': string;
  'aiTool.updateSheet': string;
  'aiTool.createToken': string;
  'aiTool.bindSheet': string;
  'aiTool.createNpc': string;
  'aiTool.initiativeUpdate': string;
  'aiTool.initiativeNext': string;
  'aiTool.initiativeReset': string;
  'aiTool.applyDamage': string;
  'aiTool.applyHealing': string;
  'aiTool.spellSlotSpend': string;
  'aiTool.concentrationSet': string;
  'aiTool.effectAdd': string;
  'aiTool.effectUpdate': string;
  'aiTool.effectRemove': string;
  'aiTool.repeatingRowAdd': string;
  'aiTool.repeatingRowUpdate': string;
  'aiTool.repeatingRowRemove': string;
  'aiTool.pageActivate': string;
  'aiTool.pageTransfer': string;
  'aiTool.pageCreate': string;
  'aiTool.pageRename': string;
  'aiTool.locationLinkCreate': string;
  'aiTool.fogReveal': string;
  'aiTool.fogReset': string;
  'aiTool.fogToggle': string;
  'aiTool.sheetRest': string;
  'aiTool.abilityAdd': string;
  'aiTool.abilityRemove': string;
  'aiTool.spellbookAdd': string;
  'aiTool.spellbookRemove': string;
  'aiTool.tokenMove': string;
  'aiTool.tokenUpdate': string;
  'aiTool.tokenDelete': string;
  'aiTool.chatMessage': string;
  'aiTool.chatRoll': string;
  'aiTool.rulerShow': string;
  'aiTool.searchJournal': string;
  'aiTool.getJournalEntry': string;
  'aiTool.getRelatedEntries': string;
  'aiTool.createJournalEntry': string;
  'aiTool.updateJournalEntry': string;
  'aiTool.revealJournalEntry': string;
  'aiTool.journalLink': string;
  'aiTool.getSpell': string;
  'aiTool.searchEquipment': string;
  'aiTool.searchMagicItems': string;
  'aiTool.searchRaces': string;
  'aiTool.searchConditions': string;
  'aiTool.createTimelineEvents': string;
  'aiTool.generateCalendar': string;

  // === Timeline Batch Preview (054) ===
  'timelineBatch.title': string;
  'timelineBatch.selectAll': string;
  'timelineBatch.deselectAll': string;
  'timelineBatch.selected': string;
  'timelineBatch.confirm': string;
  'timelineBatch.cancel': string;
  'timelineBatch.titlePlaceholder': string;

  // === Calendar AI Tab (054) ===
  'calendarAi.unavailable': string;
  'calendarAi.worldDescription': string;
  'calendarAi.worldPlaceholder': string;
  'calendarAi.minChars': string;
  'calendarAi.generating': string;
  'calendarAi.generate': string;
  'calendarAi.preview': string;
  'calendarAi.calendarName': string;
  'calendarAi.yearLabel': string;
  'calendarAi.epoch': string;
  'calendarAi.months': string;
  'calendarAi.accept': string;
  'calendarAi.regenerate': string;
  'calendarAi.replaceTitle': string;
  'calendarAi.replaceWarning': string;
  'calendarAi.replaceConfirm': string;

  // === Timeline Link Picker (054) ===
  'linkPicker.add': string;
  'linkPicker.search': string;
  'linkPicker.remove': string;
  'linkPicker.limitReached': string;
  'linkPicker.journal': string;
  'linkPicker.characters': string;
  'linkPicker.pins': string;
  'linkPicker.noResults': string;

  // === AI Map Dialog ===
  'aiMap.title': string;
  'aiMap.description': string;
  'aiMap.promptLabel': string;
  'aiMap.placeholder': string;
  'aiMap.noStrokesHint': string;
  'aiMap.errorGeneration': string;
  'aiMap.errorNetwork': string;
  'aiMap.generating': string;
  'aiMap.preview': string;
  'aiMap.regenerate': string;
  'aiMap.accept': string;
  'aiMap.generate': string;
  'aiMap.altText': string;

  // === AI Inpainting ===
  'inpaint.promptPlaceholder': string;
  'inpaint.generate': string;
  'inpaint.accept': string;
  'inpaint.regenerate': string;
  'inpaint.cancel': string;
  'inpaint.generating': string;
  'inpaint.tooSmall': string;
  'inpaint.tooLarge': string;
  'inpaint.errorGeneration': string;
  'inpaint.errorNetwork': string;

  // === AI Outpainting ===
  'outpaint.title': string;
  'outpaint.directionTop': string;
  'outpaint.directionRight': string;
  'outpaint.directionBottom': string;
  'outpaint.directionLeft': string;
  'outpaint.expandSize': string;
  'outpaint.overlap': string;
  'outpaint.prompt': string;
  'outpaint.generate': string;
  'outpaint.regenerate': string;
  'outpaint.accept': string;
  'outpaint.cancel': string;
  'outpaint.generating': string;
  'outpaint.sizeWarning': string;
  'outpaint.errorGeneration': string;
  'outpaint.errorNetwork': string;
  'outpaint.noMap': string;
  'outpaint.expandMap': string;

  // === AI Detail Pass ===
  'detail.dialogTitle': string;
  'detail.cropLabel': string;
  'detail.semanticLabel': string;
  'detail.promptPlaceholder': string;
  'detail.generate': string;
  'detail.accept': string;
  'detail.regenerate': string;
  'detail.cancel': string;
  'detail.existingLink': string;
  'detail.createNew': string;
  'detail.regenerateExisting': string;
  'detail.errorSmallRegion': string;
  'detail.errorNoMap': string;
  'detail.generating': string;
  'detail.accepting': string;

  // === SRD Panel ===
  'srd.title': string;
  'srd.monsters': string;
  'srd.spells': string;
  'srd.classes': string;
  'srd.races': string;
  'srd.equipment': string;
  'srd.magicItems': string;
  'srd.conditions': string;
  'srd.searchPlaceholder': string;
  'srd.loading': string;
  'srd.results': string;
  'srd.noResults': string;
  'srd.loadMore': string;
  'srd.back': string;
  'srd.allCR': string;
  'srd.allTypes': string;
  'srd.allLevels': string;
  'srd.cantrip': string;
  'srd.level': string;
  'srd.concentrationAbbr': string;
  'srd.allSchools': string;
  'srd.allClasses': string;
  'srd.allCategories': string;
  'srd.allRarities': string;
  'srd.noFeaturesAtLevel': string;
  'srd.token': string;
  'srd.plusSheet': string;

  // === Journal ===
  'journal.empty': string;
  'journal.pcGroup': string;
  'journal.npcGroup': string;
  // Journal panel
  'journal.panel.title': string;
  'journal.panel.createEntry': string;
  'journal.panel.allTypes': string;
  'journal.panel.searchPlaceholder': string;
  'journal.panel.noEntries': string;
  'journal.panel.noEntriesHint': string;
  'journal.panel.noResults': string;
  // Journal types
  'journal.type.npc': string;
  'journal.type.location': string;
  'journal.type.quest': string;
  'journal.type.faction': string;
  'journal.type.handout': string;
  'journal.type.lore': string;
  // Journal sections (player view)
  'journal.section.quests': string;
  'journal.section.myNotes': string;
  'journal.section.knowledge': string;
  'journal.section.onThisMap': string;
  // Journal groups
  'journal.group.ungrouped': string;
  'journal.group.new': string;
  'journal.group.namePlaceholder': string;
  'journal.group.delete': string;
  'journal.group.rename': string;
  // Journal visibility
  'journal.visibility.gm': string;
  'journal.visibility.personal': string;
  'journal.visibility.all': string;
  // Journal context menu
  'journal.menu.edit': string;
  'journal.menu.reveal': string;
  'journal.menu.hide': string;
  'chat.system.conditionEnded': string;
  'chat.system.entryRevealed': string;
  'chat.system.hitDie': string;
  'chat.system.deathSave.success': string;
  'chat.system.deathSave.failure': string;
  'chat.system.deathSave.nat20': string;
  'chat.system.deathSave.nat1': string;
  'chat.system.deathSave.stabilized': string;
  'chat.system.deathSave.died': string;
  'error.character_dead': string;
  'error.exhausted': string;
  'error.rest_at_zero_hp': string;
  'journal.menu.pin': string;
  'journal.menu.unpin': string;
  'journal.menu.delete': string;
  // Journal delete dialog
  'journal.delete.title': string;
  'journal.delete.description': string;
  'journal.delete.cancel': string;
  'journal.delete.confirm': string;
  // Journal create dialog
  'journal.create.title': string;
  'journal.create.namePlaceholder': string;
  'journal.create.submit': string;
  // Journal preview fields
  'journal.preview.race': string;
  'journal.preview.occupation': string;
  'journal.preview.disposition': string;
  'journal.preview.description': string;
  'journal.preview.secrets': string;
  'journal.preview.personality': string;
  'journal.preview.questHooks': string;
  'journal.preview.alive': string;
  'journal.preview.dead': string;
  'journal.preview.objectives': string;
  'journal.preview.gmNotes': string;
  'journal.preview.rewards': string;
  'journal.preview.attitude': string;
  'journal.preview.content': string;
  'journal.preview.category': string;
  // Journal disposition
  'journal.disposition.friendly': string;
  'journal.disposition.neutral': string;
  'journal.disposition.hostile': string;
  'journal.disposition.unknown': string;

  // Journal editor
  'journal.editor.namePlaceholder': string;
  'journal.editor.gmOnly': string;
  'journal.editor.reveal': string;
  'journal.editor.hide': string;
  'journal.editor.revealedFields': string;
  'journal.editor.revealedFieldsHint': string;
  'journal.editor.revealConfirm': string;
  'journal.editor.revealConfirmAction': string;
  'journal.editor.pin': string;
  'journal.editor.unpin': string;
  'journal.editor.openEditor': string;
  'journal.editor.visibilityGm': string;
  'journal.editor.visibilityAll': string;
  'journal.editor.tags': string;
  'journal.editor.tagsPlaceholder': string;
  'journal.editor.addItem': string;
  'journal.editor.removeItem': string;
  'journal.editor.edit': string;
  'journal.editor.done': string;
  'journal.editor.more': string;
  'journal.editor.bindToPin': string;
  'journal.editor.bindToSheet': string;
  'journal.editor.bindToPage': string;
  'journal.editor.configureFields': string;
  'journal.editor.noContent': string;

  // Journal form: NPC
  'journal.form.npc.portrait': string;
  'journal.form.npc.race': string;
  'journal.form.npc.occupation': string;
  'journal.form.npc.disposition': string;
  'journal.form.npc.description': string;
  'journal.form.npc.personality': string;
  'journal.form.npc.secrets': string;
  'journal.form.npc.questHooks': string;
  'journal.form.npc.alive': string;

  // Journal form: Location
  'journal.form.location.image': string;
  'journal.form.location.parent': string;
  'journal.form.location.description': string;
  'journal.form.location.secrets': string;
  'journal.form.location.page': string;

  // Journal form: Quest
  'journal.form.quest.status': string;
  'journal.form.quest.questGiver': string;
  'journal.form.quest.description': string;
  'journal.form.quest.gmNotes': string;
  'journal.form.quest.objectives': string;
  'journal.form.quest.objectiveText': string;
  'journal.form.quest.objectiveCompleted': string;
  'journal.form.quest.objectiveHidden': string;
  'journal.form.quest.objectiveDescription': string;
  'journal.objective.saving': string;
  'journal.form.quest.rewards': string;

  // Journal form: Faction
  'journal.form.faction.description': string;
  'journal.form.faction.attitude': string;
  'journal.form.faction.secrets': string;

  // Journal form: Handout
  'journal.form.handout.content': string;
  'journal.form.handout.image': string;
  'journal.form.handout.gmNotes': string;

  // Journal form: Lore
  'journal.form.lore.category': string;
  'journal.form.lore.content': string;
  'journal.form.lore.gmNotes': string;

  // Journal form: Disposition options
  'journal.form.disposition.friendly': string;
  'journal.form.disposition.neutral': string;
  'journal.form.disposition.hostile': string;
  'journal.form.disposition.unknown': string;

  // Journal form: Quest status options
  'journal.form.questStatus.active': string;
  'journal.form.questStatus.completed': string;
  'journal.form.questStatus.failed': string;
  'journal.form.questStatus.hidden': string;

  // Journal: Links
  'journal.links.title': string;
  'journal.links.add': string;
  'journal.links.empty': string;
  'journal.links.relation': string;
  'journal.links.target': string;
  'journal.links.customRelation': string;
  'journal.links.remove': string;
  'journal.links.incoming': string;

  // Journal field labels (for revealedFields checkboxes)
  'journal.field.description': string;
  'journal.field.race': string;
  'journal.field.occupation': string;
  'journal.field.personality': string;
  'journal.field.secrets': string;
  'journal.field.questHooks': string;
  'journal.field.alive': string;
  'journal.field.attitude': string;
  'journal.field.content': string;
  'journal.field.gmNotes': string;
  'journal.field.objectives': string;
  'journal.field.rewards': string;
  'journal.field.status': string;
  'journal.field.category': string;
  'journal.field.image': string;
  'journal.field.disposition': string;

  // Journal: Map integration
  'journal.map.bindNote': string;
  'journal.map.noteNotFound': string;
  'journal.map.goToMap': string;
  'journal.map.pageNotFound': string;

  // Journal: NPC CharacterSheet
  'journal.npc.bindSheet': string;
  'journal.npc.linkedSheet': string;
  'journal.npc.sheetDeleted': string;
  'journal.npc.openSheet': string;
  'journal.npc.unbindSheet': string;

  // Journal: Relation labels
  'journal.relation.lives_in': string;
  'journal.relation.quest_giver': string;
  'journal.relation.member_of': string;
  'journal.relation.enemy_of': string;

  // === Sheet View ===
  'sheet.speed': string;
  'sheet.initiativeMod': string;
  'sheet.tokenDescription': string;
  'sheet.tokenDescriptionPlaceholder': string;
  'sheet.tokenDescriptionHint': string;
  'sheet.fullForm': string;
  'sheet.statBlock': string;
  'sheet.tokenActions': string;
  'sheet.actionName': string;
  'sheet.actionExpression': string;
  'sheet.addAction': string;
  'sheet.hitDice': string;
  'sheet.spendHitDie': string;
  'sheet.featuresTraits': string;
  'sheet.uses': string;
  'sheet.rest': string;
  'sheet.shortRest': string;
  'sheet.longRest': string;
  'sheet.levelUp': string;
  'sheet.encumbered': string;
  'sheet.rollTooltip': string;
  'sheet.proficiency': string;

  // === Character Wizard ===
  'wizard.createTitle': string;
  'wizard.step': string;
  'wizard.createCharacter': string;
  'wizard.stepRace': string;
  'wizard.stepClass': string;
  'wizard.stepAbilities': string;
  'wizard.stepSkills': string;
  'wizard.stepEquipment': string;
  'wizard.stepSpells': string;
  'wizard.stepReview': string;
  'wizard.stepBackground': string;
  'wizard.createDescription': string;
  'wizard.rebuildSheet': string;
  'wizard.seededSaved': string;
  'wizard.seededRead': string;
  'wizard.stepIncomplete': string;
  'wizard.abilityPlus': string;
  'wizard.ac': string;
  'wizard.age': string;
  'wizard.alignment': string;
  'wizard.alsoGet': string;
  'wizard.alwaysPrepared': string;
  'wizard.ancestry': string;
  'wizard.appearance': string;
  'wizard.attacks': string;
  'wizard.backstory': string;
  'wizard.bonds': string;
  'wizard.cantrips': string;
  'wizard.classSkills': string;
  'wizard.concentration': string;
  'wizard.cost': string;
  'wizard.darkvision': string;
  'wizard.domainSpells': string;
  'wizard.equipment': string;
  'wizard.expertise': string;
  'wizard.eyes': string;
  'wizard.favoredEnemy': string;
  'wizard.favoredTerrain': string;
  'wizard.features': string;
  'wizard.fixedSkills': string;
  'wizard.flaws': string;
  'wizard.fromBackground': string;
  'wizard.ft': string;
  'wizard.gender': string;
  'wizard.gp': string;
  'wizard.grantedSkills': string;
  'wizard.hair': string;
  'wizard.height': string;
  'wizard.hitDice': string;
  'wizard.hp': string;
  'wizard.ideals': string;
  'wizard.initiative': string;
  'wizard.languages': string;
  'wizard.level1Features': string;
  'wizard.manualRange': string;
  'wizard.missingTitle': string;
  'wizard.ofChoice': string;
  'wizard.passivePerception': string;
  'wizard.personalityTraits': string;
  'wizard.pickLanguages': string;
  'wizard.pickTools': string;
  'wizard.pointsLeft': string;
  'wizard.prepareFromBook': string;
  'wizard.profBonus': string;
  'wizard.proficiencies': string;
  'wizard.raceCantrip': string;
  'wizard.raceSkills': string;
  'wizard.raceTraits': string;
  'wizard.reroll': string;
  'wizard.resistances': string;
  'wizard.roll': string;
  'wizard.rolled': string;
  'wizard.saves': string;
  'wizard.skills': string;
  'wizard.skillsCount': string;
  'wizard.slots': string;
  'wizard.spellAttack': string;
  'wizard.spellDc': string;
  'wizard.spellInfo': string;
  'wizard.spellcasting': string;
  'wizard.subrace': string;
  'wizard.summary': string;
  'wizard.tools': string;
  'wizard.weight': string;
  'wizard.shortRest': string;
  'wizard.longRest': string;
  'wizard.spellsHintWizard': string;
  'wizard.spellsHintKnown': string;
  'wizard.spellsHintPrepared': string;
  'wizard.spellbook': string;
  'wizard.spellsKnown': string;
  'wizard.spellsPrepared': string;
  'wizard.any.simple': string;
  'wizard.any.simple-melee': string;
  'wizard.any.martial': string;
  'wizard.any.martial-melee': string;
  'wizard.any.instrument': string;
  'wizard.any.arcane-focus': string;
  'wizard.any.druidic-focus': string;
  'wizard.any.holy-symbol': string;
  'wizard.dragon.Black': string;
  'wizard.dragon.Blue': string;
  'wizard.dragon.Brass': string;
  'wizard.dragon.Bronze': string;
  'wizard.dragon.Copper': string;
  'wizard.dragon.Gold': string;
  'wizard.dragon.Green': string;
  'wizard.dragon.Red': string;
  'wizard.dragon.Silver': string;
  'wizard.dragon.White': string;
  'wizard.method.standard-array': string;
  'wizard.method.point-buy': string;
  'wizard.method.roll': string;
  'wizard.method.manual': string;
  'wizard.methodHint.standard-array': string;
  'wizard.methodHint.point-buy': string;
  'wizard.methodHint.roll': string;
  'wizard.methodHint.manual': string;
  'wizard.missing.race': string;
  'wizard.missing.race.subrace': string;
  'wizard.missing.race.ancestry': string;
  'wizard.missing.race.abilityPlus': string;
  'wizard.missing.race.skills': string;
  'wizard.missing.race.cantrip': string;
  'wizard.missing.class': string;
  'wizard.missing.class.subclass': string;
  'wizard.missing.class.option': string;
  'wizard.missing.class.favoredEnemy': string;
  'wizard.missing.class.favoredTerrain': string;
  'wizard.missing.class.expertise': string;
  'wizard.missing.skills': string;
  'wizard.missing.background': string;
  'wizard.missing.languages': string;
  'wizard.missing.tools': string;
  'wizard.missing.abilities': string;
  'wizard.missing.abilities.pointBuy': string;
  'wizard.missing.spells.cantrips': string;
  'wizard.missing.spells.level1': string;
  'wizard.missing.spells.prepared': string;
  'wizard.missing.name': string;
  'wizard.subclass.cleric': string;
  'wizard.subclass.sorcerer': string;
  'wizard.subclass.warlock': string;
  'wizard.enemy.aberrations': string;
  'wizard.enemy.beasts': string;
  'wizard.enemy.celestials': string;
  'wizard.enemy.constructs': string;
  'wizard.enemy.dragons': string;
  'wizard.enemy.elementals': string;
  'wizard.enemy.fey': string;
  'wizard.enemy.fiends': string;
  'wizard.enemy.giants': string;
  'wizard.enemy.monstrosities': string;
  'wizard.enemy.oozes': string;
  'wizard.enemy.plants': string;
  'wizard.enemy.undead': string;
  'wizard.enemy.humanoids-two-races': string;
  'wizard.terrain.arctic': string;
  'wizard.terrain.coast': string;
  'wizard.terrain.desert': string;
  'wizard.terrain.forest': string;
  'wizard.terrain.grassland': string;
  'wizard.terrain.mountain': string;
  'wizard.terrain.swamp': string;
  'wizard.terrain.underdark': string;

  // === Conditions (D&D) ===
  'condition.blinded': string;
  'condition.charmed': string;
  'condition.deafened': string;
  'condition.frightened': string;
  'condition.grappled': string;
  'condition.incapacitated': string;
  'condition.invisible': string;
  'condition.paralyzed': string;
  'condition.petrified': string;
  'condition.poisoned': string;
  'condition.prone': string;
  'condition.restrained': string;
  'condition.stunned': string;
  'condition.unconscious': string;
  'condition.exhaustion': string;
  'condition.concentration': string;

  // === Toast messages ===
  'toast.mapUploaded': string;
  'toast.mapUploadError': string;
  'toast.tokenAdded': string;
  'toast.tokenCreated': string;
  'toast.sessionSaved': string;
  'toast.sessionLoaded': string;

  // === Server Error Codes ===
  'error.INVALID_TOKEN': string;
  'error.USER_NOT_FOUND': string;
  'error.sessionError': string;
  'error.SESSION_NOT_FOUND': string;
  'error.NOT_AUTHORIZED': string;
  'error.UNAUTHORIZED': string;
  'error.NO_SKETCH': string;
  'error.INVALID_PROMPT': string;
  'error.PAGE_NOT_FOUND': string;
  'error.AI_UNAVAILABLE': string;
  'error.TIMEOUT': string;
  'error.AI_ERROR': string;
  'error.AUTH_REQUIRED': string;
  'error.BAD_REQUEST': string;
  'error.NOT_FOUND': string;
  'error.FORBIDDEN': string;

  // === Pluralization ===
  'plural.participant_one': string;
  'plural.participant_few': string;
  'plural.participant_many': string;
  'plural.day_one': string;
  'plural.day_few': string;
  'plural.day_many': string;

  // === AppShell session bar ===
  'session.label': string;
  'session.participantsLabel': string;
  'session.disconnect': string;
  'session.expandSidebar': string;
  'session.collapseSidebar': string;
  'session.detachPanel': string;
  'session.panelDetached': string;
  'session.roleGM': string;
  'session.rolePlayer': string;
  'session.connectionLost': string;
  'session.connectionRestored': string;
  'session.sessionCreated': string;
  'session.sessionJoined': string;
  'session.participantJoined': string;
  'session.participantLeft': string;
  'session.pageCreated': string;
  'session.sheetCreated': string;

  // === Input Dialog ===
  'inputDialog.cancel': string;
  'inputDialog.confirm': string;
  'inputDialog.tooLong': string;

  // === Confirm Dialog ===
  'confirmDialog.replaceBinding': string;
  'confirmDialog.replaceBindingDesc': string;
  'confirmDialog.replace': string;

  // === AppDialogs ===
  'appDialogs.loading': string;

  // === File upload ===
  'upload.mapLoaded': string;
  'upload.mapError': string;
  'upload.tokenAdded': string;

  // === Misc ===
  'misc.drawingsCleared': string;
  'misc.sessionSaved': string;
  'misc.sessionLoaded': string;
  'misc.loadError': string;
  'misc.hpDialogTitle': string;
  'misc.hpDialogHint': string;
  'misc.resizeDialogTitle': string;
  'misc.newTokenDialogTitle': string;

  // === AI Confirm Card ===
  'aiConfirm.details': string;
  'aiConfirm.cancel': string;
  'aiConfirm.create': string;

  // === Chat Roll ===
  'chatRoll.crit': string;
  'chatRoll.fumble': string;
  'chatRoll.damage': string;

  // === Condition Tooltip ===
  'conditionTooltip.noDescription': string;

  // === Confirm Dialog defaults ===
  'confirmDialog.confirm': string;
  'confirmDialog.cancel': string;

  // === Effects Panel ===
  'effects.title': string;
  'effects.empty': string;
  'effects.enable': string;
  'effects.disable': string;
  'effects.remove': string;
  'effects.addEffect': string;
  'effects.source': string;
  'effects.target': string;
  'effects.mode': string;
  'effects.value': string;
  'effects.add': string;

  // === AI Map generation count ===
  'aiMap.generationCount': string;

  // === Note Editor placeholders ===
  'notes.titlePlaceholder': string;
  'notes.contentPlaceholder': string;
  'notes.noContent': string;
  'notes.quickCreate': string;
  'notes.setParent': string;
  'notes.moveToRoot': string;

  // === Map Pins ===
  'pins.title': string;
  'pins.empty': string;
  'pins.newPin': string;
  'pins.editPin': string;
  'pins.icon': string;
  'pins.color': string;
  'pins.selectColor': string;
  'pins.gmOnly': string;
  'pins.ownerOnly': string;
  'pins.visibility': string;
  'pins.linkedEntry': string;
  'pins.orphan': string;
  'pins.unlinkEntry': string;
  'pins.linkEntry': string;
  'pins.delete': string;
  'pins.deleteConfirm': string;
  'pins.deleteConfirmDesc': string;
  'pins.created': string;
  'pins.deleted': string;
  'pins.limitReached': string;
  'pins.quickCreate': string;
  'pins.noEntry': string;
  'pins.journalEntry': string;
  'pins.selectEntry': string;
  'pins.visibilityAll': string;
  'pins.showAskTitle': string;
  'pins.showAskBody': string;

  // === Fullscreen Mini Panel ===
  'fullscreen.round': string;
  'fullscreen.nextTurn': string;
  'fullscreen.exitFullscreen': string;

  // === Location Breadcrumb ===
  'breadcrumb.back': string;

  // === Navigate Request Toast ===
  'navigate.wantsToGoTo': string;
  'navigate.deny': string;
  'navigate.approve': string;

  // === NPC Stat Block ===
  'npc.conditions': string;
  'npc.attacks': string;
  'npc.rollAbility': string;
  'npc.checkAbility': string;
  'npc.rollAttack': string;
  'npc.abilityStr': string;
  'npc.abilityDex': string;
  'npc.abilityCon': string;
  'npc.abilityInt': string;
  'npc.abilityWis': string;
  'npc.abilityCha': string;

  // === PopoutWindow ===
  'popout.close': string;

  // === Portrait Upload ===
  'portrait.fileTooLarge': string;
  'portrait.networkError': string;
  'portrait.alt': string;

  // === Repeating Section ===
  'repeating.add': string;
  'repeating.empty': string;
  'repeating.remove': string;
  'repeating.showAll': string;
  'repeating.hidden': string;

  // === Selection Bar ===
  'selectionBar.inspect': string;
  'selectionBar.edit': string;
  'selectionBar.conditions': string;
  'selectionBar.delete': string;

  // === Sheet Modal ===
  'sheetModal.popout': string;
  'sheetModal.templateNotFound': string;

  // === Spell Slots Widget ===
  'spells.slotsTitle': string;
  'spells.noSlots': string;
  'spells.level': string;
  'spells.spendSlot': string;
  'spells.spent': string;

  // === Token Inspect Popup ===
  'tokenInspect.gmNotes': string;
  'tokenInspect.openSheet': string;
  'tokenInspect.ac': string;

  // === Sidebar detached tab ===
  'sidebar.detached': string;
  'sidebar.notification': string;

  // === SrdPanel filter placeholders ===
  'srd.filterType': string;
  'srd.filterLevel': string;
  'srd.filterSchool': string;
  'srd.filterClass': string;
  'srd.filterCategory': string;
  'srd.filterRarity': string;

  // === Dialog close ===
  'dialog.close': string;

  // === Token Panel preset ===
  'tokens.presetToken': string;

  // === Session Page Tabs ===
  'sessionPage.sessionsTab': string;
  'sessionPage.profileTab': string;

  // === App (lobby) ===
  'app.profile': string;
  'app.logout': string;
  'app.joinByCode': string;

  // === AI Error Messages ===
  'aiError.serviceUnavailable': string;
  'aiError.noModelKey': string;
  'aiError.noModelPlayer': string;
  'aiError.rateLimit': string;
  'aiError.networkError': string;
  'aiError.timeout': string;
  'aiError.sendError': string;
  'aiError.gmOnly': string;
  'aiError.voicePermissionDenied': string;
  'aiError.voiceNotAvailable': string;
  'aiError.tokenBudget': string;
  'aiError.playerAiDisabled': string;

  // === Player AI ===
  'playerAi.title': string;
  'playerAi.send': string;
  'playerAi.newChat': string;

  // === Game System ===
  'gameSystem.label': string;
  'gameSystem.dnd5e': string;
  'gameSystem.pf2e': string;
  'gameSystem.generic': string;
  'srd.unavailable': string;

  // === Auth Errors ===
  'authError.loginError': string;
  'authError.registerError': string;
  'authError.updateError': string;
  'authError.networkError': string;

  // === Chat ===
  'chat.defaultName': string;

  // === Chat Sound Settings ===
  'chat.sound.settings': string;
  'chat.sound.volume': string;
  'chat.sound.mute': string;
  'chat.sound.chatEnabled': string;
  'chat.sound.diceEnabled': string;

  // === Ruler / Measurement ===
  'ruler.ft': string;
  'ruler.total': string;

  // === Token copy ===
  'tokens.copySuffix': string;

  // === Sidebar: Alerts ===
  'sidebar.alerts': string;

  // === Sidebar: Timeline ===
  'sidebar.timeline': string;
  'sidebar.plan': string;

  // === Alerts ===
  'alerts.title': string;
  'alerts.create': string;
  'alerts.edit': string;
  'alerts.delete': string;
  'alerts.trigger': string;
  'alerts.emptyTitle': string;
  'alerts.emptyHint': string;
  'alerts.presets': string;
  'alerts.triggerActive': string;
  'alerts.nameLabel': string;
  'alerts.visualLabel': string;
  'alerts.soundLabel': string;
  'alerts.layoutLabel': string;
  'alerts.durationLabel': string;
  'alerts.volume': string;
  'alerts.triggerLabel': string;
  'alerts.customEventPlaceholder': string;
  'alerts.triggerEnabledLabel': string;
  'alerts.deleteConfirmTitle': string;
  'alerts.deleteConfirmDesc': string;

  // Alert presets
  'alerts.preset.critical-hit': string;
  'alerts.preset.character-death': string;
  'alerts.preset.combat-start': string;
  'alerts.preset.new-location': string;

  // Alert layouts
  'alerts.layout.fullscreen': string;
  'alerts.layout.banner-top': string;
  'alerts.layout.banner-bottom': string;

  // Alert duration
  'alerts.duration.auto': string;
  'alerts.duration.manual': string;

  // Alert triggers
  'alerts.trigger.manual': string;
  'alerts.trigger.initiative:start': string;
  'alerts.trigger.initiative:round': string;
  'alerts.trigger.token:death': string;
  'alerts.trigger.roll:critical': string;
  'alerts.trigger.roll:fumble': string;
  'alerts.trigger.page:activate': string;
  'trigger.askShow': string;
  'trigger.show': string;
  'trigger.notNow': string;
  'alerts.trigger.custom': string;

  // Alert sections
  'alerts.mediaSection': string;
  'alerts.settingsSection': string;
  'alerts.triggerSection': string;
  'alerts.limitCounter': string;

  // Alert limits
  'alerts.limitWarning': string;
  'alerts.limitReached': string;
  'alerts.triggerLimitReached': string;

  // Alert sound settings
  'alerts.sound.settings': string;
  'alerts.sound.volume': string;
  'alerts.sound.mute': string;

  // Alert errors
  'alerts.error.nameRequired': string;
  'alerts.error.durationRange': string;
  'alerts.error.customEventRequired': string;
  'alerts.error.fileTooLarge': string;
  'alerts.error.uploadFailed': string;

  // === Stickers ===
  'sticker.title': string;
  'sticker.cooldown': string;
  'sticker.cooldownUnit': string;
  'sticker.createPack': string;
  'sticker.connectPack': string;
  'sticker.editPack': string;
  'sticker.deletePack': string;
  'sticker.disconnectPack': string;
  'sticker.emptyTitle': string;
  'sticker.emptyHint': string;
  'sticker.noStickers': string;
  'sticker.packName': string;
  'sticker.packNamePlaceholder': string;
  'sticker.stickerCount': string;
  'sticker.uploadHint': string;
  'sticker.uploadDragHint': string;
  'sticker.uploadBrowse': string;
  'sticker.limitReached': string;
  'sticker.connectTitle': string;
  'sticker.connectButton': string;
  'sticker.allConnected': string;
  'sticker.noPacks': string;
  'sticker.createFirst': string;
  'sticker.deleteConfirmTitle': string;
  'sticker.deleteConfirmMessage': string;
  'sticker.disconnectConfirmTitle': string;
  'sticker.disconnectConfirmMessage': string;
  'sticker.builtIn': string;
  'sticker.custom': string;
  'sticker.done': string;
  'sticker.cancel': string;
  'sticker.close': string;
  'sticker.unavailable': string;
  'sticker.emptyPack': string;
  'sticker.error.formatInvalid': string;
  'sticker.error.fileTooLarge': string;
  'sticker.error.tooManyUploads': string;
  'sticker.error.packNotFound': string;
  'sticker.error.uploadFailed': string;
  'sticker.error.limitExceeded': string;
  'sticker.mediaTab.alerts': string;
  'sticker.mediaTab.stickers': string;

  // === Release Notes ===
  'releaseNotes.badgeNew': string;
  'releaseNotes.title': string;
  'releaseNotes.menuItem': string;
  'releaseNotes.sectionNew': string;
  'releaseNotes.sectionFix': string;
  'releaseNotes.sectionImprovement': string;
  'releaseNotes.currentVersion': string;
  'releaseNotes.learnMore': string;
  'releaseNotes.version': string;
  'releaseNotes.empty': string;

  // === Mobile ===
  'mobile.overflowMenu': string;
  'mobile.aiChat': string;
  'mobile.chat': string;
  'mobile.sheet': string;
  'mobile.initiative': string;
  'mobile.dice': string;
  'mobile.voiceInput': string;
  'mobile.voiceListening': string;
  'mobile.voiceError': string;
  'mobile.voiceNotSupported': string;

  'mobile.tokenPlaced': string;

  // === Mobile Canvas (024-2) ===
  'mobile.tokenPopup.hp': string;
  'mobile.tokenPopup.close': string;
  'mobile.tokenPopup.conditions': string;

  // === Mobile Touch Interactions (024-3) ===
  'mobile.characterSheet.aiHint': string;
  'mobile.characterSheet.aiHintDismiss': string;

  // === PWA (024-4) ===
  'pwa.offlineBanner': string;
  'pwa.installPrompt': string;
  'pwa.installButton': string;
  'pwa.installDismiss': string;

  // === Push Notifications (024-5) ===
  'push.promptTitle': string;
  'push.promptAllow': string;
  'push.promptDismiss': string;

  // === Bug Report ===
  'bugReport.title': string;
  'bugReport.description': string;
  'bugReport.titleLabel': string;
  'bugReport.titlePlaceholder': string;
  'bugReport.descriptionLabel': string;
  'bugReport.descriptionPlaceholder': string;
  'bugReport.cancel': string;
  'bugReport.submit': string;
  'bugReport.submitted': string;
  'bugReport.menuItem': string;
  'bugReport.rateLimited': string;
  'bugReport.retryIn': string;

  // === Undo/Redo (032-A) ===
  'undo.tokenMove': string;

  // === AoE Templates (037) ===
  'aoe.cone': string;
  'aoe.sphere': string;
  'aoe.cube': string;
  'aoe.line': string;
  'aoe.cylinder': string;
  'aoe.size': string;
  'aoe.color': string;
  'aoe.label': string;
  'aoe.clearAll': string;
  'aoe.autoRemove': string;
  'aoe.durationNone': string;
  'aoe.duration1Round': string;
  'aoe.duration1Min': string;
  'aoe.duration10Min': string;
  'spellAoe.aim': string;
  'spellAoe.noToken': string;
  'spellAoe.noTokenGm': string;
  'spellAoe.keep': string;
  'spellAoe.cancel': string;
  'spellAoe.title': string;
  'spellAoe.hintAim': string;
  'spellAoe.hintCone': string;
  'spellAoe.outOfRange': string;
  'spellAoe.offer': string;
  'spellAoe.offerAction': string;
  'spellAoe.hintOriginTurn': string;

  // === Roll Card (037) ===
  'rollCard.hit': string;
  'rollCard.miss': string;
  'rollCard.damage': string;
  'rollCard.crit': string;
  'rollCard.fumble': string;
  'rollCard.breakdown': string;
  'rollCard.audit': string;

  // === Action HUD (037) ===
  'actionHud.attack': string;
  'actionHud.spell': string;
  'actionHud.ability': string;
  'actionHud.other': string;
  'actionHud.compact': string;
  'actionHud.expanded': string;
  'actionHud.icon': string;
  'actionHud.color': string;

  // === Death Save (037) ===
  'deathSave.dying': string;
  'deathSave.stable': string;
  'deathSave.dead': string;
  'deathSave.alive': string;
  'deathSave.roll': string;
  'deathSave.success': string;
  'deathSave.failure': string;
  'deathSave.nat20': string;
  'deathSave.nat1': string;
  'deathSave.stabilized': string;
  'deathSave.died': string;

  // === Concentration (037) ===
  'concentration.broken': string;
  'concentration.maintained': string;
  'concentration.dc': string;

  // === Condition: Dead (037) ===
  'condition.dead': string;

  // === Mobile Content Access (034-phase-4) ===
  'mobile.switchLanguage': string;
  'mobile.notes': string;
  'mobile.journal': string;
  'mobile.map': string;
  'mobile.timeline': string;
  'mobile.srd': string;
  'sheetModal.clickToEditName': string;

  // === Map Objects ===
  'mapObject.empty': string;
  'mapObject.createObject': string;
  'mapObject.title': string;
  'mapObject.deleteConfirmTitle': string;
  'mapObject.deleteConfirmDesc': string;
  'mapObject.visibleAll': string;
  'mapObject.gmOnly': string;
  'mapObject.noun': string;
  'mapObject.label': string;
  'mapObject.description': string;
  'mapObject.category': string;
  'mapObject.properties': string;
  'mapObject.cover': string;
  'mapObject.coverNone': string;
  'mapObject.coverHalf': string;
  'mapObject.coverThreeQuarter': string;
  'mapObject.coverFull': string;
  'mapObject.state': string;
  'mapObject.stateNone': string;
  'mapObject.stateClosed': string;
  'mapObject.stateOpen': string;
  'mapObject.stateBroken': string;
  'mapObject.stateLit': string;
  'mapObject.stateExtinguished': string;
  'mapObject.traversable': string;
  'mapObject.difficultTerrain': string;
  'mapObject.hp': string;
  'mapObject.maxHp': string;
  'mapObject.locked': string;
  'mapObject.damage': string;
  'mapObject.light': string;
  'mapObject.lightRadius': string;
  'mapObject.lightColor': string;
  'mapObject.display': string;
  'mapObject.displayColor': string;
  'mapObject.icon': string;
  'mapObject.opacity': string;
  'mapObject.visibility': string;
  'mapObject.visibilityGm': string;
  'mapObject.visibilityAll': string;
  'mapObject.interactable': string;
  'mapObject.catFurniture': string;
  'mapObject.catTerrain': string;
  'mapObject.catHazard': string;
  'mapObject.catDoor': string;
  'mapObject.catContainer': string;
  'mapObject.catLight': string;
  'mapObject.catCustom': string;
  'mapObject.coverHalfBadge': string;
  'mapObject.coverThreeQuarterBadge': string;
  'mapObject.coverFullBadge': string;

  // === Fog Confirm Dialogs ===
  'fog.confirmCoverAll.title': string;
  'fog.confirmCoverAll.description': string;
  'fog.confirmClearAll.title': string;
  'fog.confirmClearAll.description': string;
  'fog.confirm.confirm': string;
  'fog.confirm.cancel': string;

  // === Quick Access Panel ===
  'quickAccess.title': string;
  'quickAccess.empty': string;
  'quickAccess.add': string;
  'quickAccess.remove': string;
  'quickAccess.collapse': string;
  'quickAccess.expand': string;
  'quickAccess.close': string;
  'quickAccess.categoryTool': string;
  'quickAccess.categoryRoll': string;
  'quickAccess.categoryMacro': string;
  'quickAccess.rollLabel': string;
  'quickAccess.rollFormula': string;
  'quickAccess.macroLabel': string;
  'quickAccess.macroCommands': string;
  'quickAccess.invalidFormula': string;
  'quickAccess.limitReached': string;
  'quickAccess.duplicateTool': string;
  'quickAccess.macroCommandCount': string;
  'quickAccess.categoryAction': string;
  'quickAccess.categoryTab': string;
  'quickAccess.sectionPinned': string;
  'quickAccess.sectionRecent': string;
  'quickAccess.noSheetsWithActions': string;
  'quickAccess.duplicateAction': string;
  'quickAccess.duplicateTab': string;
  'shortcuts.toggleQuickAccess': string;

  // === Settings Dialog ===
  'settings.title': string;
  'settings.tab.ai': string;
  'settings.tab.campaign': string;
  'settings.tab.display': string;
  'settings.tab.combat': string;
  'settings.tab.permissions': string;
  'settings.tab.resources': string;
  'settings.resources.gmOnly': string;
  'settings.resources.loadError': string;
  'settings.resources.tokens30d': string;
  'settings.resources.money30d': string;
  'settings.resources.moneyText': string;
  'settings.resources.moneyImage': string;
  'settings.resources.moneyVoice': string;
  'settings.resources.moneyBilled': string;
  'settings.resources.players': string;
  'settings.resources.tokensAllTime': string;
  'settings.resources.allTimeHint': string;
  'settings.resources.interactions30d': string;
  'settings.resources.chartTitle': string;
  'settings.resources.tokensUnit': string;
  'settings.resources.interactionsUnit': string;
  'settings.resources.tableToggle': string;
  'settings.resources.colDay': string;
  'settings.resources.colTokens': string;
  'settings.resources.colInteractions': string;
  'settings.ai.worldDescription': string;
  'settings.ai.worldDescriptionPlaceholder': string;
  'settings.ai.houseRules': string;
  'settings.ai.houseRulesPlaceholder': string;
  'settings.ai.tone': string;
  'settings.ai.gmInstructions': string;
  'settings.ai.gmInstructionsPlaceholder': string;
  'settings.campaign.measurementMode': string;
  'settings.campaign.gridSizeFt': string;
  'settings.campaign.navigationMode': string;
  'settings.campaign.navFree': string;
  'settings.campaign.navConfirm': string;
  'settings.campaign.navGmOnly': string;
  'settings.campaign.importCampaign': string;
  'settings.display.gridStyle': string;
  'settings.display.gridLines': string;
  'settings.display.gridDots': string;
  'settings.display.gridNone': string;
  'settings.display.gridOpacity': string;
  'settings.display.sizeTiny': string;
  'settings.display.sizeSmall': string;
  'settings.display.sizeMedium': string;
  'settings.display.sizeLarge': string;
  'settings.display.sizeHuge': string;
  'settings.display.sizeGargantuan': string;
  'settings.display.ambientOverlay': string;
  'settings.display.hpBarVisibility': string;
  'settings.display.hpAlways': string;
  'settings.display.hpHover': string;
  'settings.display.hpGmOnly': string;
  'settings.display.hpNever': string;
  'settings.display.hpBarHint': string;
  'settings.display.defaultTokenSize': string;
  'settings.combat.autoRollInitiative': string;
  'settings.combat.autoRollInitiativeDesc': string;
  'settings.combat.confirmDamage': string;
  'settings.combat.confirmDamageDesc': string;
  'settings.combat.critRange': string;
  'settings.combat.critRangeDesc': string;
  'settings.combat.deathSavesEnabled': string;
  'settings.combat.deathSavesEnabledDesc': string;
  'settings.permissions.description': string;
  'settings.permissions.moveOtherTokens': string;
  'settings.permissions.moveOtherTokensDesc': string;
  'settings.permissions.drawOnMap': string;
  'settings.permissions.drawOnMapDesc': string;
  'settings.permissions.addTokens': string;
  'settings.permissions.addTokensDesc': string;
  'settings.permissions.createNotes': string;
  'settings.permissions.createNotesDesc': string;
  'settings.permissions.useAi': string;
  'settings.permissions.useAiDesc': string;
  'settings.permissions.playerAiRate': string;
  'settings.permissions.playerAiRateDesc': string;
  'settings.permissions.playerAiBudget': string;
  'settings.permissions.playerAiBudgetDesc': string;
  'settings.permissions.unlimited': string;
  'settings.permissions.allowPlayerStickers': string;
  'settings.permissions.allowPlayerStickersDesc': string;
  'settings.permissions.viewOtherSheets': string;
  'settings.permissions.viewOtherSheetsDesc': string;
  'settings.permissions.viewNone': string;
  'settings.permissions.viewBasic': string;
  'settings.permissions.viewFull': string;

  'settings.ai.excludedEntities': string;
  'settings.ai.searchEntities': string;
  'settings.ai.sectionPriority': string;
  'settings.ai.briefingFocus': string;
  'settings.ai.briefingFocusDesc': string;
  'settings.ai.briefingFocusPlaceholder': string;
  'settings.ai.briefingActiveNpcs': string;
  'settings.ai.briefingActiveNpcsDesc': string;
  'settings.ai.briefingActiveNpcsPlaceholder': string;
  'settings.ai.snapshotsLabel': string;
  'settings.ai.deleteWithoutAsking': string;
  'settings.ai.deleteWithoutAskingDesc': string;
  'snapshot.title': string;
  'snapshot.refresh': string;
  'snapshot.empty': string;
  'snapshot.undo': string;
  'snapshot.restoring': string;
  'snapshot.age.now': string;
  'snapshot.age.min': string;
  'snapshot.age.h': string;
  'snapshot.age.d': string;
  'snapshot.label.homebrewConnect': string;
  'snapshot.label.homebrewUpdate': string;
  'snapshot.label.draftAccept': string;
  'snapshot.label.levelUp': string;
  'snapshot.label.createCharacter': string;
  'snapshot.label.encounter': string;
  'snapshot.label.import': string;
  'snapshot.kind.sheets': string;
  'snapshot.kind.pages': string;
  'snapshot.kind.tokens': string;
  'snapshot.kind.chat': string;
  'snapshot.kind.initiative': string;
  'snapshot.confirm.title': string;
  'snapshot.confirm.what': string;
  'snapshot.confirm.counting': string;
  'snapshot.confirm.nothing': string;
  'snapshot.confirm.removed': string;
  'snapshot.confirm.reverted': string;
  'snapshot.confirm.returned': string;
  'snapshot.confirm.deleteOne': string;
  'snapshot.confirm.deleteFew': string;
  'snapshot.confirm.deleteMany': string;
  'snapshot.confirm.plain': string;
  'toolOptions.allSettings': string;
  'combat.confirmDamagePrompt': string;
  'permissions.denied.drawOnMap': string;
  'permissions.denied.addTokens': string;
  'permissions.denied.createNotes': string;

  // === Server Error Codes (command:error) ===
  'error.not_found': string;
  'error.material_is_prep_source': string;
  'error.map_not_found': string,
  'error.map_not_in_beat': string,
  'error.unauthorized': string;
  'error.OWNERSHIP_CHECK_FAILED': string;
  'error.forbidden': string;
  'error.invalid_input': string;
  'error.invalid_name': string;
  'error.invalid_data': string;
  'error.invalid_type': string;
  'error.invalid_tags': string;
  'error.invalid_visibility': string;
  'error.duplicate': string;
  'error.duplicate_link': string;
  'error.self_link': string;
  'error.self_reference': string;
  'error.circular_reference': string;
  'error.LINK_NOT_FOUND': string;
  'error.links_limit': string;
  'error.PAGE_LIMIT': string;
  'error.CANNOT_DELETE_LAST': string;
  'error.PLAYERS_ON_PAGE': string;
  'error.MOVE_TARGET_NOT_FOUND': string;
  'error.PLAYERS_MOVED': string;
  'error.INVALID_PAGE_LIST': string;
  'error.SAME_PAGE': string;
  'error.INVALID_NAME': string;
  'error.NAME_TOO_LONG': string;
  'error.TOKEN_NOT_FOUND': string;
  'error.SHEET_NOT_FOUND': string;
  'error.alert_limit': string;
  'error.trigger_limit': string;
  'error.unknown_preset': string;
  'error.invalid_label': string;
  'error.invalid_color': string;
  'error.invalid_icon': string;
  'error.invalid_content': string;
  'error.invalid_title': string;
  'error.note_limit': string;
  'error.parent_not_found': string;
  'error.invalid_area': string;
  'error.invalid_description': string;
  'error.invalid_terrain': string;
  'error.invalid_cover': string;
  'error.TARGET_PAGE_NOT_FOUND': string;
  'error.NAVIGATE_REQUEST_NOT_FOUND': string;
  'error.GM_ONLY_NAVIGATION': string;
  'error.builtin_template': string;
  'error.in_use': string;
  'error.validation_error': string;
  'error.invalid_state': string;
  'error.death_saves_disabled': string;
  'error.system_not_supported': string;
  'error.hit_dice_exhausted': string;
  'error.no_hit_dice': string;
  'error.no_spell_slots': string;
  'error.no_pact': string;
  'error.not_repeating': string;
  'error.invalid_action': string;
  'error.invalid_expression': string;
  'error.invalid_level': string;
  'error.invalid_mode': string;
  'error.not_draft': string;
  'error.session_limit': string;
  'error.session_full': string;
  'error.active_limit': string;
  'error.timeline_limit': string;
  'error.tags_limit': string;
  'error.PIN_LIMIT': string;
  'error.PIN_NOT_FOUND': string;
  'error.WALL_NOT_FOUND': string;
  'error.WALL_INVALID': string;
  'error.WALL_LIMIT': string;
  'error.WALL_NOT_DOOR': string;
  'error.WALL_CHAIN_MIN_POINTS': string;
  'error.INVALID_ILLUMINATION': string;
  'error.invalid_condition': string;
  'error.invalid_transition': string;
  'error.restore_failed': string;
  'error.max_level': string;
  'error.trigger_disabled': string;
  'error.internal_error': string;

  // === Timeline ===
  'timeline.eventType.sessionStart': string;
  'timeline.eventType.sessionRecap': string;
  'timeline.eventType.encounter': string;
  'timeline.eventType.discovery': string;
  'timeline.eventType.plot': string;
  'timeline.eventType.travel': string;
  'timeline.eventType.rest': string;
  'timeline.eventType.downtime': string;
  'timeline.eventType.character': string;
  'timeline.eventType.custom': string;
  'timeline.eventType.chapter': string;
  'timeline.timeOfDay.dawn': string;
  'timeline.timeOfDay.morning': string;
  'timeline.timeOfDay.midday': string;
  'timeline.timeOfDay.afternoon': string;
  'timeline.timeOfDay.dusk': string;
  'timeline.timeOfDay.evening': string;
  'timeline.timeOfDay.night': string;
  'timeline.timeOfDay.midnight': string;
  'timeline.form.editTitle': string;
  'timeline.form.newTitle': string;
  'timeline.form.titleLabel': string;
  'timeline.form.titlePlaceholder': string;
  'timeline.form.typeLabel': string;
  'timeline.form.descriptionLabel': string;
  'timeline.form.descriptionPlaceholder': string;
  'timeline.form.yearLabel': string;
  'timeline.form.monthLabel': string;
  'timeline.form.dayLabel': string;
  'timeline.form.timeOfDayLabel': string;
  'timeline.form.manualTimeOverride': string;
  'timeline.form.customTimePlaceholder': string;
  'timeline.form.timeTextLabel': string;
  'timeline.form.timeLabelPlaceholder': string;
  'timeline.form.visibilityLabel': string;
  'timeline.form.visibilityAll': string;
  'timeline.form.visibilityGmOnly': string;
  'timeline.form.visibilityPlayers': string;
  'timeline.form.tagsLabel': string;
  'timeline.form.tagsPlaceholder': string;
  'timeline.form.linksLabel': string;
  'timeline.form.sessionLabel': string;
  'timeline.form.noSession': string;
  'timeline.form.cancelButton': string;
  'timeline.form.saveButton': string;
  'timeline.form.createButton': string;
  'timeline.form.suggestButton': string;
  'timeline.form.suggestTitle': string;
  'timeline.form.linkTitle': string;
  'timeline.form.whatHappenedLabel': string;
  'timeline.form.whatSuggestLabel': string;
  'timeline.form.addButton': string;
  'timeline.form.suggestToGmButton': string;
  'timeline.form.details': string;
  'timeline.form.atTime': string;
  'timeline.form.staysDraft': string;
  'timeline.form.customTimeHint': string;
  'timeline.form.visibleToAll': string;
  'timeline.form.selectedPlayers': string;
  'timeline.form.noPlayers': string;
  'timeline.form.linkWith': string;
  'timeline.form.findLink': string;
  'timeline.form.findLinkPlaceholder': string;
  'timeline.form.linkName': string;
  'timeline.form.linkKind.beat': string;
  'timeline.form.linkKind.map': string;
  'timeline.form.linkKind.record': string;
  'timeline.form.linkKind.quest': string;
  'timeline.form.linkKind.character': string;
  'timeline.form.linkKind.pin': string;
  'timeline.form.linkWhy.inProgress': string;
  'timeline.form.linkWhy.players': string;
  'timeline.form.linkWhy.quest': string;
  'timeline.form.linkWhy.selected': string;
  'timeline.form.unlink': string;
  'timeline.filters.title': string;
  'timeline.filters.resetAriaLabel': string;
  'timeline.filters.reset': string;
  'timeline.filters.searchPlaceholder': string;
  'timeline.filters.searchAriaLabel': string;
  'timeline.filters.typeGroupLabel': string;
  'timeline.filters.tagsGroupLabel': string;
  'timeline.filters.allSessions': string;
  'timeline.filters.typeLabel': string;
  'timeline.filters.tagLabel': string;
  'timeline.filters.myEventsOnly': string;
  'timeline.cursor.dayFormat': string;
  'timeline.cursor.editDateTooltip': string;
  'timeline.cursor.addDate': string;
  'timeline.cursor.partsGroup': string;
  'timeline.cursor.prevPart': string;
  'timeline.cursor.nextPart': string;
  'rest.shiftOffer': string;
  'rest.shiftAction': string;
  'timeline.cursor.partNextDay': string;
  'timeline.cursor.undo': string;
  'timeline.cursor.prevDay': string;
  'timeline.cursor.nextDay': string;
  'timeline.cursor.month': string;
  'timeline.cursor.year': string;
  'timeline.cursor.day': string;
  'timeline.cursor.datePicker': string;
  'timeline.cursor.closePicker': string;
  'timeline.cursor.inTheWorld': string;
  'timeline.list.noDate': string;
  'timeline.list.addEventToDay': string;
  'timeline.list.now': string;
  'timeline.list.deleteConfirm': string;
  'timeline.list.draftBadge': string;
  'timeline.list.editTooltip': string;
  'timeline.list.more': string;
  'timeline.list.less': string;
  'timeline.list.deleteTooltip': string;
  'timeline.drafts.regionLabel': string;
  'timeline.drafts.title': string;
  'timeline.drafts.approveAriaLabel': string;
  'timeline.drafts.rejectAriaLabel': string;
  'timeline.panel.emptyTitle': string;
  'timeline.panel.emptySubtext': string;
  'timeline.panel.regionLabel': string;
  'timeline.panel.noTime': string;
  'timeline.panel.createEventAriaLabel': string;
  'timeline.panel.noFilterResults': string;
  'timeline.panel.emptySubtextGM': string;
  'timeline.panel.createEventButton': string;
  'timeline.autoTransitions.removeAll': string;
  'timeline.autoTransitions.removeAllTitle': string;
  'timeline.autoTransitions.removeAllDesc': string;
  'timeline.autoTransitions.removeAllConfirm': string;
  // Letopis panel (GM Studio «Летопись»)
  'timeline.panel.title': string;
  'timeline.panel.addEvent': string;
  'timeline.panel.suggestEvent': string;
  'timeline.panel.more': string;
  'timeline.panel.calendarLine': string;
  'timeline.panel.noCalendar': string;
  'timeline.panel.changeCalendar': string;
  'timeline.panel.published': string;
  'timeline.calendar.title': string;
  'timeline.calendar.current': string;
  'timeline.calendar.monthsInfo': string;
  'timeline.calendar.remove': string;
  'timeline.calendar.edit': string;
  'timeline.calendar.choosePreset': string;
  'timeline.calendar.presetInfo': string;
  'timeline.calendar.createOwn': string;
  'timeline.calendar.customName': string;
  'timeline.calendar.customMonth': string;
  'timeline.calendar.aiGenerate': string;
  'timeline.calendar.name': string;
  'timeline.calendar.yearLabel': string;
  'timeline.calendar.epoch': string;
  'timeline.calendar.months': string;
  'timeline.calendar.addMonth': string;
  'timeline.calendar.removeMonth': string;
  'timeline.calendar.intercalary': string;
  'timeline.calendar.newMonth': string;
  'timeline.calendar.save': string;
  'timeline.calendar.cancel': string;
  'timeline.calendar.yearLabelPlaceholder': string;
  'timeline.calendar.epochPlaceholder': string;
  'timeline.calendar.intercalaryShort': string;
  'timeline.session.line': string;
  'timeline.session.statusActive': string;
  'timeline.session.statusPlanned': string;
  'timeline.session.statusCompleted': string;
  'timeline.session.none': string;
  'timeline.session.start': string;
  'timeline.session.startEvent': string;
  'timeline.session.startEventTitled': string;
  'plan.session.none': string;
  'timeline.summary.button': string;
  'timeline.summary.title': string;
  'timeline.summary.hint': string;
  'timeline.summary.played': string;
  'timeline.summary.playedNone': string;
  'timeline.summary.empty': string;
  'timeline.summary.uncheckedStays': string;
  'timeline.summary.publishAndFinish': string;
  'timeline.summary.sureCount': string;
  'timeline.summary.sureNone': string;
  'timeline.summary.sureYes': string;
  'timeline.summary.sureBack': string;
  'timeline.drafts.hint': string;
  'timeline.drafts.sourceAuto': string;
  'timeline.drafts.sourceGm': string;
  'timeline.drafts.sourcePlayer': string;
  'timeline.drafts.accept': string;
  'timeline.drafts.edit': string;
  'timeline.drafts.reject': string;
  'timeline.drafts.publish': string;
  'timeline.drafts.remove': string;
  'timeline.drafts.publishAllAuto': string;
  'timeline.drafts.publishTitle': string;
  'timeline.drafts.acceptTitle': string;
  'timeline.drafts.publishDesc': string;
  'timeline.drafts.publishAllTitle': string;
  'timeline.drafts.publishAllDesc': string;
  'timeline.drafts.rejectTitle': string;
  'timeline.drafts.rejectReason': string;
  'timeline.drafts.rejectHint': string;
  'timeline.drafts.rejected': string;
  'timeline.drafts.rejectedWithReason': string;
  'timeline.mine.title': string;
  'timeline.mine.waiting': string;
  'timeline.filters.myEventsHint': string;
  'timeline.visibility.all': string;
  'timeline.visibility.gm': string;
  'timeline.visibility.players': string;
  'timeline.visibility.menuLabel': string;
  'timeline.visibility.choosePlayers': string;
  'timeline.visibility.noPlayers': string;
  'timeline.visibility.apply': string;
  'timeline.visibility.confirmAllTitle': string;
  'timeline.visibility.confirmPlayersTitle': string;
  'timeline.visibility.confirmAllDesc': string;
  'timeline.visibility.confirmPlayersDesc': string;
  'timeline.visibility.confirm': string;
  'timeline.links.add': string;
  'timeline.links.record': string;
  'timeline.links.recordGone': string;
  'timeline.create.confirmTitle': string;
  'timeline.create.confirmDesc': string;
  'timeline.create.keepDraft': string;

  // === Campaign Sessions ===
  'campaignSessions.ariaLabel': string;
  'campaignSessions.title': string;
  'campaignSessions.newSession': string;
  'campaignSessions.titleLabel': string;
  'campaignSessions.namePlaceholder': string;
  'campaignSessions.dateLabel': string;
  'campaignSessions.active': string;
  'campaignSessions.complete': string;
  'campaignSessions.planned': string;
  'campaignSessions.start': string;
  'campaignSessions.completed': string;
  'campaignSessions.noSessions': string;
  'campaignSessions.endTitle': string;
  'campaignSessions.endUnpublished': string;
  'campaignSessions.endPublish': string;
  'campaignSessions.endRemove': string;
  'campaignSessions.endKeepDrafts': string;

  // === Import Wizard ===
  'importWizard.title': string;
  'importWizard.sourceText': string;
  'importWizard.selectSource': string;
  'importWizard.sourceDndBeyondSoon': string;
  'importWizard.sourcePdfSoon': string;
  'importWizard.sourceImageSoon': string;
  'importWizard.extractingRelations': string;
  'importWizard.analyzingRelations': string;
  'importWizard.wave1': string;
  'importWizard.wave2': string;
  'importWizard.wave3': string;
  'importWizard.importError': string;
  'importWizard.source': string;
  'importWizard.dragDropHint': string;
  'importWizard.analyzing': string;
  'importWizard.analyze': string;
  'importWizard.conflictsFound': string;
  'importWizard.tableType': string;
  'importWizard.tableName': string;
  'importWizard.tableAction': string;
  'importWizard.tableNewName': string;
  'importWizard.strategySkip': string;
  'importWizard.strategyMerge': string;
  'importWizard.strategyRename': string;
  'importWizard.newNamePlaceholder': string;
  'importWizard.applying': string;
  'importWizard.apply': string;
  'importWizard.importPreview': string;
  'importWizard.importComplete': string;
  'importWizard.stepSource': string;
  'importWizard.stepUpload': string;
  'importWizard.stepConflicts': string;
  'importWizard.stepResult': string;
  'importWizard.strategyOverwrite': string;
  'importWizard.previewSheets': string;
  'importWizard.previewTokens': string;
  'importWizard.previewPages': string;
  'importWizard.previewNotes': string;
  'importWizard.resultAdded': string;
  'importWizard.resultOverwritten': string;
  'importWizard.resultSkipped': string;

  // === Bug Report additions ===
  'bugReport.sectionDescription': string;
  'bugReport.sectionElement': string;
  'bugReport.sectionContext': string;
  'bugReport.noDescription': string;
  'bugReport.roleGM': string;
  'bugReport.rolePlayer': string;

  // === SRD labels ===
  'srd.traits': string;
  'srd.actions': string;
  'srd.reactions': string;
  'srd.legendaryActions': string;
  'srd.castingTime': string;
  'srd.range': string;
  'srd.components': string;
  'srd.duration': string;
  'srd.resistances': string;
  'srd.immunities': string;
  'srd.senses': string;
  'srd.languages': string;
  'srd.cr': string;

  // === Spellbook ===
  'spellbook.title': string;
  'spellbook.empty': string;
  'spellbook.cantrips': string;
  'spellbook.concentration': string;

  // === Compendium Browser ===
  'compendium.equipment': string;
  'compendium.spells': string;
  'compendium.searchEquipment': string;
  'compendium.searchSpells': string;
  'compendium.noEquipment': string;
  'compendium.noSpells': string;
  'compendium.addToInventory': string;
  'compendium.addToSpellbook': string;
  'compendium.cost': string;
  'compendium.weight': string;
  'compendium.ac': string;
  'compendium.homebrew': string;
  'compendium.concentration': string;
  'compendium.properties': string;

  // === Chat additions ===
  'chat.search': string;
  'chat.searchPlaceholder': string;
  'chat.unpinMessage': string;
  'chat.pinMessage': string;
  'chat.gmPrefix': string;
  'chat.exportTitle': string;

  // === Character Wizard additions ===
  'wizard.characterNameLabel': string;
  'wizard.characterNamePlaceholder': string;
  'wizard.backgroundLabel': string;
  'wizard.backgroundPlaceholder': string;
  'wizard.raceLabel': string;
  'wizard.speed': string;
  'wizard.classLabel': string;
  'wizard.hitDie': string;
  'wizard.selectClassFirst': string;
  'wizard.noEquipmentPack': string;
  'wizard.equipped': string;

  // === Campaign Import ===
  'campaignImport.textInputHint': string;
  'campaignImport.placeholder': string;
  'campaignImport.uploadFile': string;
  'campaignImport.limitExceeded': string;
  'campaignImport.analyzing': string;
  'campaignImport.analyze': string;

  // === Homebrew Import ===
  'homebrewImport.title': string;
  'homebrewImport.inputLabel': string;
  'homebrewImport.placeholder': string;
  'homebrewImport.or': string;
  'homebrewImport.uploadPdf': string;
  'homebrewImport.extract': string;
  'homebrewImport.uploading': string;
  'homebrewImport.backgroundNote': string;
  'homebrewImport.extracting': string;
  'homebrewImport.preparingChunks': string;
  'homebrewImport.packageNameLabel': string;
  'homebrewImport.save': string;
  'homebrewImport.saving': string;
  'homebrewImport.complete': string;
  'homebrewImport.back': string;
  'homebrewImport.clear': string;
  'homebrewImport.chunk': string;
  'homebrewImport.entities': string;

  // === Entity Review ===
  'entityReview.selectAll': string;
  'entityReview.deselectAll': string;
  'entityReview.addManual': string;
  'entityReview.selectedCount': string;
  'entityReview.retryBelow': string;
  'entityReview.retry': string;

  // === Character Portraits ===
  'characterPortraits.changeColor': string;
  'characterPortraits.online': string;
  'characterPortraits.ac': string;
  'characterPortraits.publicOnly': string;
  'characterPortraits.offline': string;
  'characterPortraits.chip': string;
  'characterPortraits.chipFold': string;
  // Visibility menu items
  'characterPortraits.visibility.always': string;
  'characterPortraits.visibility.gmOnly': string;
  'characterPortraits.visibility.hidden': string;
  'characterPortraits.visibilityLabel': string;
  'characterPortraits.sheetLabel': string;
  'characterPortraits.sheetVisibleToPlayers': string;
  'characterPortraits.sheetHiddenBadge': string;
  'characterPortraits.sizeLabel': string;
  'characterPortraits.size.small': string;
  'characterPortraits.size.medium': string;
  'characterPortraits.size.large': string;
  'characterPortraits.actingAs': string;
  'characterPortraits.hiddenFromPlayers': string;
  // Tooltips
  'characterPortraits.tooltip.actorFrame': string;
  'characterPortraits.tooltip.translucency': string;

  // === Text Input Overlay ===
  'textInput.ariaLabel': string;
  'textInput.placeholder': string;

  // === Dice Panel ===
  'dice.formulaPlaceholder': string;

  // === Lobby ===
  'lobby.sessionCodePlaceholder': string;

  // === Hook Errors ===
  'hookError.catalogFailed': string;
  'hookError.noEntitiesFound': string;
  'hookError.detailFailed': string;
  'hookError.noResponseBody': string;
  'hookError.relationFailed': string;
  'hookError.applyFailed': string;
  'hookError.fileTooLarge': string;
  'hookError.serverError': string;
  'hookError.networkError': string;
  'hookError.extractionFailed': string;
  'hookError.streamEnded': string;
  'hookError.retryFailed': string;
  'hookError.saveFailed': string;
  'hookError.loadFailed': string;
  'hookError.createFailed': string;
  'hookError.uploadFailed': string;
  'hookError.generationFailed': string;
  'hookError.canvasContext': string;
  'hookError.mapLoadFailed': string;
  'hookError.pageCreateTimeout': string;
  'hookError.lassoMinPoints': string;
  'hookError.exportFailed': string;
  'homebrew.defaultPackageName': string;

  // === Homebrew Library / Import Flow (P2) ===
  'homebrew.libraryTitle': string;
  'homebrew.import': string;
  'homebrew.importJson': string;
  'homebrew.libraryEmpty': string;
  'homebrew.loading': string;
  'homebrew.export': string;
  'homebrew.delete': string;
  'homebrew.confirm': string;
  'homebrew.cancel': string;
  'homebrew.entities': string;
  'homebrew.connectTitle': string;
  'homebrew.connectLoading': string;
  'homebrew.connectEmpty': string;
  'homebrew.checkUpdates': string;
  'homebrew.extracting': string;
  'homebrew.reviewReady': string;
  'homebrew.type.class': string;
  'homebrew.type.subclass': string;
  'homebrew.type.spell': string;
  'homebrew.type.feat': string;
  'homebrew.type.race': string;
  'homebrew.type.item': string;
  'homebrew.connectFailed': string;
  'homebrew.updateFailed': string;

  // === Lazy chunk loading ===
  'error.chunkLoadFailed': string;

  // === Common additions ===
  'common.retry': string;

  // === Character HUD (066) ===
  'characterHud.pillHp': string;
  'characterHud.expand': string;
  'characterHud.collapse': string;
  'characterHud.openFullSheet': string;
  'characterHud.startCombat': string;
  'characterHud.expandDocked': string;
  'characterHud.showCharacter': string;
  'characterHud.combatBanner': string;
  'characterHud.combatBannerTurnOf': string;
  'characterHud.combatBannerIdle': string;
  'characterHud.round': string;
  'characterHud.tileHp': string;
  'wizard.rerollUsed': string;
  'wizard.notProficient': string;
  'wizard.missing.equipment': string;
  'characterHud.castAsRitual': string;
  'characterHud.resurrect': string;
  'characterHud.resurrectHp': string;
  'error.dying_no_hit_dice': string;
  'levelUp.aria': string;
  'levelUp.title': string;
  'levelUp.hp': string;
  'levelUp.hitDieCon': string;
  'levelUp.average': string;
  'levelUp.roll': string;
  'levelUp.subclass': string;
  'levelUp.newFeatures': string;
  'levelUp.spellSlots': string;
  'levelUp.slotLine': string;
  'levelUp.cantrips': string;
  'levelUp.asi': string;
  'levelUp.asiTwo': string;
  'levelUp.asiOneOne': string;
  'levelUp.confirm': string;
  'characterHud.rollDamage': string;
  'characterHud.noSlotForSpell': string;
  'characterHud.concentrationReplaced': string;
  'characterHud.concentrationOn': string;
  'characterHud.concentrationEnd': string;
  'characterHud.spellTapHint': string;
  'characterHud.deadNoRoll': string;
  'characterHud.hpInputLabel': string;
  'characterHud.hpInputPlaceholder': string;
  'characterHud.hpInputHint': string;
  'characterHud.unconsciousNoAct': string;
  'characterHud.rollMode': string;
  'characterHud.rollModeNormal': string;
  'characterHud.rollModeAdv': string;
  'characterHud.rollModeDis': string;
  'characterHud.rollModeHint': string;
  'rollCard.advantage': string;
  'rollCard.disadvantage': string;
  'rollCard.modeManual': string;
  'rollCard.keptHint': string;
  'characterHud.hpDamage': string;
  'characterHud.hpHeal': string;
  'characterHud.hpCritAtZero': string;
  'characterHud.hpDeadHint': string;
  'characterHud.hpMax': string;
  'characterHud.hpMaxShort': string;
  'characterHud.hpTempShort': string;
  'characterHud.saveRollTitle': string;
  'characterHud.tileAc': string;
  'characterHud.tileInit': string;
  'characterHud.tileConc': string;
  'characterHud.tileSpeed': string;
  'characterHud.actionAction': string;
  'characterHud.actionBonus': string;
  'characterHud.actionReaction': string;
  'characterHud.available': string;
  'characterHud.spent': string;
  'characterHud.attacks': string;
  'characterHud.spellSlots': string;
  'characterHud.effects': string;
  'characterHud.noConcentration': string;
  'characterHud.mockRoll': string;
  'characterHud.mockRollDesc': string;
  'characterHud.tabCombat': string;
  'characterHud.tabCharacter': string;
  'characterHud.tabCharShort': string;
  'characterHud.tabRoleplay': string;
  'characterHud.tabBuilder': string;
  'characterHud.backToHud': string;
  'characterHud.inDevelopment': string;
  'characterHud.stats': string;
  'characterHud.topSkills': string;
  'characterHud.saves': string;
  'characterHud.skills': string;
  'characterHud.searchSkills': string;
  'characterHud.pinSkill': string;
  'characterHud.unpinSkill': string;
  'characterHud.spells': string;
  'characterHud.detailCantrip': string;
  'characterHud.detailCircle': string;
  'characterHud.detailConcentration': string;
  'characterHud.detailRitual': string;
  'characterHud.detailCastingTime': string;
  'characterHud.detailRange': string;
  'characterHud.detailDuration': string;
  'characterHud.detailComponents': string;
  'characterHud.detailUses': string;
  'characterHud.detailHigherLevels': string;
  'characterHud.conditionAdd': string;
  'characterHud.conditionRemove': string;
  'characterHud.conditionRounds': string;
  'characterHud.conditionApply': string;
  'characterHud.collapseList': string;
  'characterHud.movement': string;
  'characterHud.movementHint': string;
  'characterHud.resetTurn': string;
  'characterHud.features': string;
  'characterHud.moreFeatures': string;
  'characterHud.spellsKnown': string;
  'characterHud.demoTitle': string;
  'characterHud.demoPill': string;
  'characterHud.demoMini': string;
  'characterHud.demoCombat': string;
  'characterHud.demoDocked': string;
  'characterHud.demoToggle': string;
  'characterHud.demoFullSheet': string;
  'characterHud.demoNpc': string;
  'characterHud.demoReset': string;
  'characterHud.npcAlly': string;
  'characterHud.npcWarm': string;
  'characterHud.npcNeutral': string;
  'characterHud.npcFoe': string;
  'characterHud.npcEnemy': string;
  'characterHud.close': string;
  'characterHud.hpTemp': string;
  'characterHud.level': string;
  'characterHud.storeCorrupted': string;

  // === Builder Mode (066 phase-4) ===
  'characterHud.builder.stepIdentity': string;
  'characterHud.builder.stepRace': string;
  'characterHud.builder.stepClass': string;
  'characterHud.builder.stepStats': string;
  'characterHud.builder.stepBackground': string;
  'characterHud.builder.stepSkills': string;
  'characterHud.builder.stepReview': string;
  'characterHud.builder.alignLG': string;
  'characterHud.builder.alignNG': string;
  'characterHud.builder.alignCG': string;
  'characterHud.builder.alignLN': string;
  'characterHud.builder.alignN': string;
  'characterHud.builder.alignCN': string;
  'characterHud.builder.alignLE': string;
  'characterHud.builder.alignNE': string;
  'characterHud.builder.alignCE': string;
  'characterHud.builder.methodPointBuy': string;
  'characterHud.builder.methodStandardArray': string;
  'characterHud.builder.methodManual': string;
  'characterHud.builder.identityTitle': string;
  'characterHud.builder.identitySubtitle': string;
  'characterHud.builder.labelName': string;
  'characterHud.builder.labelGender': string;
  'characterHud.builder.labelAlignment': string;
  'characterHud.builder.placeholderName': string;
  'characterHud.builder.raceTitle': string;
  'characterHud.builder.raceSubtitle': string;
  'characterHud.builder.subraceLabel': string;
  'characterHud.builder.raceSpeed': string;
  'characterHud.builder.raceSize': string;
  'characterHud.builder.raceLanguages': string;
  'characterHud.builder.raceTraits': string;
  'characterHud.builder.classTitle': string;
  'characterHud.builder.classSubtitle': string;
  'characterHud.builder.classSaves': string;
  'characterHud.builder.classProficiencies': string;
  'characterHud.builder.classSkillChoices': string;
  'characterHud.builder.classHitDie': string;
  'characterHud.builder.statsTitle': string;
  'characterHud.builder.statsSubtitle': string;
  'characterHud.builder.budgetRemaining': string;
  'characterHud.builder.budgetDone': string;
  'characterHud.builder.bgTitle': string;
  'characterHud.builder.bgSubtitle': string;
  'characterHud.builder.bgSkills': string;
  'characterHud.builder.bgFeature': string;
  'characterHud.builder.skillsTitle': string;
  'characterHud.builder.skillsSubtitle': string;
  'characterHud.builder.skillsCounter': string;
  'characterHud.builder.skillFromBg': string;
  'characterHud.builder.skillFromRace': string;
  'characterHud.builder.reviewTitle': string;
  'characterHud.builder.reviewSubtitle': string;
  'characterHud.builder.reviewIdentity': string;
  'characterHud.builder.reviewStats': string;
  'characterHud.builder.reviewCombat': string;
  'characterHud.builder.reviewSkills': string;
  'characterHud.builder.reviewProficiencies': string;
  'characterHud.builder.reviewSaves': string;
  'characterHud.builder.reviewErrors': string;
  'characterHud.builder.next': string;
  'characterHud.builder.prev': string;
  'characterHud.builder.save': string;
  'characterHud.builder.intro': string;
  'characterHud.builder.readOnly': string;
  'characterHud.builder.rebuildTitle': string;
  'characterHud.builder.rebuildDesc': string;
  'characterHud.builder.rebuildLevel': string;
  'characterHud.builder.keepEquipment': string;
  'characterHud.builder.saved': string;
  'characterHud.builder.previewPlaceholder': string;
  'characterHud.builder.previewSkills': string;
  'characterHud.builder.previewProficiencies': string;
  'characterHud.builder.previewHp': string;
  'characterHud.builder.previewAc': string;
  'characterHud.builder.previewSpeed': string;
  'characterHud.builder.errEmptyName': string;
  'characterHud.builder.errNoRace': string;
  'characterHud.builder.errNoClass': string;
  'characterHud.builder.errPointBuy': string;
  'characterHud.builder.errStandardArray': string;
  'characterHud.builder.errManualRange': string;
  'characterHud.builder.errNoBackground': string;
  'characterHud.builder.errSkillsUnder': string;
  'characterHud.builder.errSkillsOver': string;
  'characterHud.builder.confirmOverwrite': string;
  'characterHud.builder.confirmDiscard': string;
  'characterHud.builder.skillOverlap': string;

  // === Write Infrastructure (073) ===
  'characterHud.writeTimeout': string;
  'characterHud.sheetDeleted': string;
  'characterHud.writeRejected': string;
  'characterHud.rollTimeout': string;
  'characterHud.restShort': string;
  'characterHud.restLong': string;
  'characterHud.deathSave': string;
  'characterHud.deathDying': string;
  'characterHud.deathStable': string;
  'characterHud.deathDead': string;
  'characterHud.deathSavesTally': string;
  'characterHud.hitDiceSpend': string;
  'characterHud.inspirationOn': string;
  'characterHud.inspirationOff': string;

  // === Character Mode (066 phase-2) ===
  'characterMode.navIdentity': string;
  'characterMode.navStats': string;
  'characterMode.navSkills': string;
  'characterMode.navDefenses': string;
  'characterMode.navAbilities': string;
  'characterMode.navSpells': string;
  'characterMode.navInventory': string;
  'characterMode.navProficiencies': string;
  'characterMode.navProgression': string;
  'characterMode.navResources': string;
  'characterMode.levelUp': string;
  'characterMode.rest': string;
  'characterMode.hitDie': string;
  'characterMode.levelUpShort': string;
  'characterMode.passivePerception': string;
  'characterMode.passiveShort': string;
  'characterMode.passiveInvestigation': string;
  'characterMode.passiveInsight': string;
  'characterMode.filterAll': string;
  'characterMode.filterProficient': string;
  'characterMode.filterByAbility': string;
  'characterMode.breakdownTotal': string;
  'characterMode.breakdownStatMod': string;
  'characterMode.breakdownProficiency': string;
  'characterMode.breakdownExpertise': string;
  'characterMode.breakdownEffects': string;
  'characterMode.breakdownBase': string;
  'characterMode.spellDC': string;
  'characterMode.spellAttack': string;
  'characterMode.concentration': string;
  'characterMode.ritual': string;
  'characterMode.prepared': string;
  'characterMode.known': string;
  'characterMode.cantrips': string;
  'characterMode.spellLevel': string;
  'characterMode.noSpells': string;
  'characterMode.noFeatures': string;
  'characterMode.noItems': string;
  'characterMode.weight': string;
  'characterMode.currency': string;
  'characterMode.equipped': string;
  'characterMode.attunement': string;
  'characterMode.armor': string;
  'characterMode.weapons': string;
  'characterMode.tools': string;
  'characterMode.languages': string;
  'characterMode.currentLevel': string;
  'characterMode.levelManualHint': string;
  'characterMode.nextLevel': string;
  'characterMode.xpProgress': string;
  'characterMode.resistances': string;
  'characterMode.immunities': string;
  'characterMode.vulnerabilities': string;
  'characterMode.conditions': string;
  'characterMode.speeds': string;
  'characterMode.senses': string;
  'characterMode.classFeatures': string;
  'characterMode.raceFeatures': string;
  'characterMode.feats': string;
  'characterMode.allFeatures': string;
  'characterMode.inspiration': string;
  'characterMode.hitDice': string;
  'characterMode.mockLevelUp': string;
  'characterMode.search': string;
  'characterMode.proficiencyBonus': string;
  'characterMode.walkSpeed': string;
  'characterMode.unitFt': string;
  'characterMode.unitLbs': string;
  'characterMode.unitGp': string;
  'characterMode.armorClass': string;
  'characterMode.castingTime': string;
  'characterMode.range': string;
  'characterMode.castSpell': string;
  'characterMode.slotAriaLabel': string;
  'characterMode.useAriaLabel': string;
  'characterMode.available': string;
  'characterMode.spent': string;
  'characterMode.shortRest': string;
  'characterMode.longRest': string;
  'characterMode.asiOrFeat': string;
  'characterMode.attunementSlot': string;
  'characterMode.attunementFilled': string;
  'characterMode.attunementEmpty': string;
  'characterMode.bdModifier': string;
  'characterMode.bdValue': string;
  'characterMode.bdSave': string;
  'characterMode.bdMod': string;
  'characterMode.bdProficiency': string;
  'characterMode.bdExpertise': string;
  'characterMode.bdPassive': string;
  'characterMode.bdSkillMod': string;
  'characterMode.bdBaseAC': string;
  'characterMode.bdShield': string;
  'characterMode.bdItems': string;
  'characterMode.bdUnarmored': string;
  'characterMode.bdArmor': string;
  'characterMode.bdMiscAc': string;
  'characterMode.bdEffects': string;
  'characterMode.bdAttack': string;
  'characterMode.bdStatMod': string;
  'characterMode.bdSpellAttack': string;

  // === Roleplay Mode (066 phase-3) ===
  'roleplay.boardTitle': string;
  'roleplay.boardSubtitle': string;
  'roleplay.cardHero': string;
  'roleplay.cardTriada': string;
  'roleplay.cardManners': string;
  'roleplay.cardBackstory': string;
  'roleplay.cardGoals': string;
  'roleplay.cardNpc': string;
  'roleplay.cardFactions': string;
  'roleplay.cardSessionLog': string;
  'roleplay.cardMoments': string;
  'roleplay.catAll': string;
  'roleplay.catPersonality': string;
  'roleplay.catConnections': string;
  'roleplay.catPast': string;
  'roleplay.catPresent': string;
  'roleplay.catFuture': string;
  'roleplay.tagPersonality': string;
  'roleplay.tagConnections': string;
  'roleplay.tagPast': string;
  'roleplay.tagPresent': string;
  'roleplay.tagFuture': string;
  'roleplay.done': string;
  'roleplay.doneEsc': string;
  'roleplay.spanQuarter': string;
  'roleplay.spanThird': string;
  'roleplay.spanHalf': string;
  'roleplay.spanTwoThirds': string;
  'roleplay.spanFull': string;
  'roleplay.stylePlain': string;
  'roleplay.styleParchment': string;
  'roleplay.styleDark': string;
  'roleplay.addRow': string;
  'roleplay.addGoal': string;
  'roleplay.addNpc': string;
  'roleplay.addFaction': string;
  'roleplay.addMoment': string;
  'roleplay.addSession': string;
  'roleplay.duplicate': string;
  'roleplay.pin': string;
  'roleplay.unpin': string;
  'roleplay.changeType': string;
  'roleplay.deleteCard': string;
  'roleplay.deleteConfirmTitle': string;
  'roleplay.deleteConfirmDesc': string;
  'roleplay.changeTypeConfirm': string;
  'roleplay.momentPrologue': string;
  'roleplay.momentMinor': string;
  'roleplay.momentEncounter': string;
  'roleplay.momentDilemma': string;
  'roleplay.momentRevelation': string;
  'roleplay.momentTurning': string;
  'roleplay.momentFinale': string;
  'roleplay.relAlly': string;
  'roleplay.relWarm': string;
  'roleplay.relNeutral': string;
  'roleplay.relEnemy': string;
  'roleplay.searchPlaceholder': string;
  'roleplay.autoLayout': string;
  'roleplay.addCard': string;
  'roleplay.addCardTitle': string;
  'roleplay.addCardHeading': string;
  'roleplay.addCardType': string;
  'roleplay.addCardContent': string;
  'roleplay.addCardSize': string;
  'roleplay.addCardStyle': string;
  'roleplay.addCardSubmit': string;
  'roleplay.addCardCancel': string;
  'roleplay.addCardTitlePlaceholder': string;
  'roleplay.addCardContentPlaceholder': string;
  'roleplay.editPlaceholder': string;
  'roleplay.noResults': string;
  'roleplay.emptyBoard': string;
  'roleplay.labelIdeal': string;
  'roleplay.labelBond': string;
  'roleplay.labelFlaw': string;
  'roleplay.priorityArc': string;
  'roleplay.prioritySession': string;
  'roleplay.sessionNumber': string;
  'roleplay.sessionLabel': string;
  'roleplay.placeholderTitle': string;
  'roleplay.placeholderSummary': string;
  'roleplay.placeholderDescription': string;
  'roleplay.placeholderName': string;
  'roleplay.placeholderNotes': string;
  'roleplay.placeholderFaction': string;
  'roleplay.placeholderGoal': string;
  'roleplay.dragToReorder': string;
  'roleplay.editOptions': string;
  'roleplay.removeGoal': string;
  'roleplay.loyaltyLevel': string;
  'roleplay.footerCount': string;

  // === Studio (T008) ===
  'studio.breadcrumb.prep': string;
  'studio.topbar.renameCampaign': string;
  'studio.topbar.unnamed': string;
  'studio.topbar.renameFailed': string;
  'studio.enter': string;
  'studio.beat.add': string;
  'studio.beat.cycleStatus': string;
  'studio.rail.search': string;
  'studio.sheet.features': string;
  'studio.sheet.inventory': string;
  'studio.sheet.gold': string;
  'studio.sheet.openFull': string;
  'studio.sheet.backToStudio': string;
  'studio.workingSet.empty': string;
  'studio.workspace.placeholder': string;
  'studio.entity.openFull': string;
  'studio.breadcrumb.sessionLabel': string;
  'studio.toTable': string;
  // === Studio AI Assistant (T019) ===
  'studio.aiDrafts.stub': string;
  'studio.ai.title': string;
  'studio.ai.groupApplied': string;
  'studio.ai.accept': string;
  'studio.ai.reject': string;
  'studio.ai.revert': string;
  'studio.ai.badgeConflict': string;
  'studio.ai.badgeReview': string;
  'studio.ai.badgeDraft': string;
  'tia.status.idle': string;
  'tia.status.thinking': string;
  'tia.status.done': string;
  'tia.status.error': string;
  'tia.status.notes.one': string;
  'tia.status.notes.few': string;
  'tia.status.notes.many': string;
  'tia.say.thinking': string;
  'tia.say.doneN': string;
  'tia.say.done': string;
  'tia.say.error': string;
  'tia.say.noKey': string;
  'tia.say.notes': string;
  'tia.collapse': string;
  'tia.expand': string;
  'ai.allowEdits': string;
  'ai.allowEditsHint': string;
  'ai.placeholderAdvise': string;
  'ai.focus.prefix': string;
  'ai.focus.beat': string;
  'ai.focus.quest': string;
  'ai.focus.record': string;
  'ai.focus.map': string;
  'ai.focus.sheet': string;
  'ai.focus.library': string;
  'ai.focus.remarks': string;
  'ai.focus.playingNow': string;
  'ai.focus.playing': string;
  'ai.focus.table': string;
  'ai.focus.myMap': string;
  'ai.focus.playersMap': string;
  'ai.focus.selected': string;
  'ai.emptyStudio': string;
  'ai.chip.beat1': string;
  'ai.chip.beat2': string;
  'ai.chip.beat3': string;
  'ai.chip.quest1': string;
  'ai.chip.quest2': string;
  'ai.chip.npc1': string;
  'ai.chip.npc2': string;
  'ai.chip.lib1': string;
  'ai.chip.lib2': string;
  'ai.chip.table1': string;
  'ai.chip.table2': string;
  'ai.chip.table3': string;
  'ai.plan.title': string;
  'ai.plan.do': string;
  'ai.plan.done': string;
  'ai.plan.stale.beat': string;
  'ai.plan.stale.objective': string;
  'ai.plan.stale.missing': string;
  'ai.plan.stale.gone': string;
  'ai.outward.stale': string;
  'ai.focus.beatN': string;
  'ai.plan.step.reserve': string;
  'ai.plan.step.outcome': string;
  'ai.plan.step.outcomeBare': string;
  'ai.plan.step.note': string;
  'ai.plan.step.objective': string;
  'ai.plan.step.objectiveBare': string;
  'ai.plan.step.current': string;
  'ai.plan.step.chronicle': string;
  'ai.plan.step.edit': string;
  'ai.plan.step.sheet': string;
  'ai.plan.step.other': string;
  'ai.plan.step.linkQuest': string;
  'ai.outward.show': string;
  'ai.outward.showCaption': string;
  'ai.outward.delete': string;
  'ai.outward.deleteCaption': string;
  'ai.outward.confirmShow': string;
  'ai.outward.confirmShowBody': string;
  'ai.outward.confirmDelete': string;
  'ai.outward.confirmDeleteBody': string;
  'ai.outward.confirmShowButton': string;
  'ai.outward.confirmDeleteButton': string;
  'ai.outward.doneShow': string;
  'ai.outward.doneDelete': string;
  'ai.outward.running': string;
  'ai.outward.partial': string;
  'ai.outward.unknown': string;
  'ai.outward.failed': string;
  'ai.outward.changed': string;
  'ai.outward.gone': string;
  'ai.outward.already': string;
  'ai.outward.saveFailed': string;
  'ai.outward.fromSource': string;
  'ai.outward.sourceUnknown': string;
  'ai.plan.unknown.all': string;
  'ai.plan.unknown.partial': string;
  'ai.plan.unknown.none': string;
  'ai.topic.line': string;
  'ai.topic.close': string;
  'ai.topic.discuss': string;
  'ai.topic.ended': string;
  'ai.topic.endedDone': string;
  'ai.topic.closed': string;
  'ai.remark.fixLine': string;
  'ai.remark.hintLine': string;
  'aiChange.sheetValue': string;
  // AI drafts
  'aiDrafts.title': string;
  'aiDrafts.pendingCount': string;
  'aiDrafts.badgeReview': string;
  'aiDrafts.badgeDraft': string;
  'studio.aiDrafts.count': string;
  'studio.rail.plan': string;
  'studio.rail.library': string;
  'studio.beat.status.draft': string;
  'studio.beat.status.ready': string;
  'studio.beat.status.played': string;
  'studio.beat.status.carried': string;
  'studio.beat.chooseStatus': string;
  'plan.title': string;
  'plan.empty.title': string;
  'plan.empty.body': string;
  'plan.openStudio': string;
  'plan.openInStudio': string;
  'plan.current.label': string;
  'plan.current.none': string;
  'plan.makeCurrent': string;
  'plan.endCurrent': string;
  'plan.pending': string;
  'plan.refused': string;
  'plan.lostAnswer': string;
  'plan.prep': string;
  'plan.prep.none': string;
  'plan.unavailable': string;
  'plan.map.deleted': string;
  'plan.map.openHere': string;
  'plan.map.show': string;
  'plan.map.showing': string;
  'plan.maps': string,
  'mapView.editsSaved': string;
  'mapView.viewedDeleted': string;
  'plan.next.button': string;
  'plan.next.last': string;
  'plan.next.markPlayed': string;
  'plan.next.back': string;
  'plan.next.another': string;
  'plan.next.hint': string;
  'plan.list.label': string;
  'plan.list.view': string;
  'plan.list.now': string;
  'plan.viewing': string;
  'plan.viewing.back': string;
  'plan.pick.hint': string;
  'plan.sec.quest': string;
  'plan.sec.maps': string;
  'plan.sec.noMaps': string;
  'plan.sec.notes': string;
  'plan.sec.npcs': string;
  'plan.sec.quick': string;
  'plan.npc.hint': string;
  'plan.quick.add': string;
  'plan.quick.into': string;
  'plan.quick.pick': string;
  'plan.quick.placeholder': string;
  'plan.hint.showMap': string;
  'plan.hint.dismiss': string;
  'map.row.playersHere': string;
  'map.row.viewing': string;
  'map.row.openForMe': string;
  'map.row.more': string;
  'map.row.playersAlreadyHere': string;
  'map.row.studioNote': string;
  'maps.beat.title': string;
  'maps.beat.none': string;
  'maps.shared.title': string;
  'maps.others.show': string;
  'maps.others.hide': string;
  'maps.on.title': string;
  'maps.studioHint': string;
  'studio.mapMenu.rename': string;
  'studio.mapMenu.moveUp': string;
  'studio.mapMenu.moveDown': string;
  'studio.mapMenu.nest': string;
  'studio.mapMenu.delete': string;
  'studio.mapMenu.aiMap': string;
  'studio.mapMenu.clearDrawings': string;
  'studio.mapMenu.imagePosition': string;
  'studio.mapDialog.renameTitle': string;
  'studio.mapDialog.nestTitle': string;
  'studio.mapDialog.parent': string;
  'studio.mapDialog.topLevel': string;
  'studio.mapDialog.positionTitle': string;
  'studio.mapDialog.moveImage': string;
  'studio.mapDialog.moveImageHint': string;
  'studio.mapDialog.moveImageActive': string;
  'studio.mapDialog.save': string;
  'studio.mapDialog.cancel': string;
  'studio.mapDialog.nameLabel': string;
  'studio.mapCrumbs.label': string;
  'studio.mapDrawer.open': string;
  'studio.mapDialog.px': string;
  'beat.next.title': string;
  'beat.next.outcome': string;
  'beat.next.addFork': string;
  'beat.next.formLabel': string;
  'beat.next.if': string;
  'beat.next.ifPlaceholder': string;
  'beat.next.leadsTo': string;
  'beat.next.newReserve': string;
  'beat.next.reserveName': string;
  'beat.next.reservePlaceholder': string;
  'beat.next.optionMain': string;
  'beat.next.optionReserve': string;
  'beat.next.noForks': string;
  'beat.next.removeFork': string;
  'beat.next.add': string;
  'beat.next.cancel': string;
  'beat.reserve.pill': string;
  'beat.reserve.title': string;
  'beat.reserve.from': string;
  'beat.reserve.railCaption': string;
  'mapView.label': string;
  'mapView.youSee': string;
  'mapView.playersSee': string;
  'mapView.together': string;
  'mapView.playerSees': string;
  'mapView.togetherHint': string;
  'mapView.ownViewHint': string;
  'mapView.returnToPlayers': string;
  'mapView.show': string;
  'mapView.showing': string;
  'mapView.confirmTitle': string;
  'mapView.confirmBody': string;
  'mapView.confirm': string;
  'mapView.showWithParty': string;
  'mapView.showMapOnly': string;
  'mapView.shownCarried': string;
  'mapView.shownWithoutParty': string;
  'mapView.carried': string;
  'mapView.openPlayersMap': string;
  'mapView.shown': string;
  'mapView.shownDraft': string;
  'mapView.backTo': string;
  'studio.workingSet.pin': string;
  'studio.workingSet.unpin': string;
  'studio.tabs.noTabs': string;
  'studio.tabs.close': string;
  'studio.tabs.preview': string;
  'studio.tabs.suggestedFromBeat': string;
  'studio.tabs.kindEntry': string;
  'studio.tabs.selected': string;
  'studio.tabs.beside': string;
  'studio.tabs.temporary': string;
  'studio.tabs.playersMap': string;
  'studio.ref.named': string;
  'studio.side.region': string;
  'studio.side.temporary': string;
  'studio.side.temporaryHint': string;
  'studio.side.pinned': string;
  'studio.side.close': string;
  'studio.full.restore': string;
  'studio.full.restoreLabel': string;
  'studio.full.exit': string;
  'studio.full.openBeside': string;
  'studio.full.openBesideLabel': string;
  'studio.full.besidePalette': string;
  'studio.replace.title': string;
  'studio.replace.body': string;
  'studio.mapMenu.label': string;
  'studio.mapMenu.noSnapshots': string;
  'studio.create.entryPrefix': string;
  'studio.create.sheetPc': string;
  'studio.create.sheetNpc': string;
  'studio.create.map': string;
  'studio.create.namePrompt': string;
  'studio.create.failed': string;
  'studio.create.retry': string;
  'studio.create.unresolved': string;
  'studio.create.unresolvedTitle': string;
  'studio.create.goneSince': string;
  'studio.rail.create': string;
  'studio.library.createAny': string;
  'studio.beat.delete': string;
  'studio.beat.deleteConfirm': string;
  'studio.entity.cardPreview': string;
  'studio.entity.tokenCount': string;
  'studio.entity.openWorkshop': string;
  'studio.entity.openWorkshopHint': string;
  'studio.entity.snapshots': string;
  'studio.entity.snapshotCreate': string;
  'studio.entity.snapshotCreateHint': string;
  'studio.entity.snapshotPrompt': string;

  // === Studio Map (T003) ===
  'studio.map.empty': string;
  'studio.map.legend': string;
  'studio.peek.ac': string;
  'studio.peek.hp': string;
  'studio.peek.hpMax': string;
  'studio.entry.delete': string;
  'studio.entry.deleteTitle': string;
  'studio.entry.deleteBody': string;
  'studio.peek.speed': string;
  'studio.palette.hint': string;
  'studio.palette.placeholder': string;
  'studio.palette.empty': string;
  'studio.palette.createBeat': string;
  'studio.palette.kind.beat': string;
  'studio.palette.kind.sheet': string;
  'studio.palette.kind.journal': string;
  'studio.palette.kind.page': string;
  'studio.palette.kind.action': string;
  'studio.sheet.mentionedIn': string;
  'studio.beat.one': string;
  'studio.beat.overview': string;
  'studio.map.targetMissing': string;
  'studio.beat.rename': string;
  'studio.beatPanel.note': string;
  'studio.beatPanel.none': string;
  'studio.beatPanel.hint': string;
  'studio.beat.prepEntryName': string;
  'studio.beatPanel.prep': string;
  'studio.beatPanel.prepPlaceholder': string;
  'studio.beatPanel.prepAdd': string;
  'studio.beatPanel.prepPending': string;
  'studio.beatPanel.prepLoading': string;
  'studio.beatPanel.prepFailedEntry': string;
  'studio.beatPanel.prepFailedLink': string;
  'studio.beatPanel.prepFailedToast': string;
  'studio.beatPanel.prepRetry': string;
  'studio.beatPanel.prepMissing': string;
  'studio.beatPanel.prepSource': string;
  'studio.beatPanel.prepOpen': string;
  'studio.beatPanel.prepShared': string;
  'studio.beatPanel.prepOtherType': string;
  'studio.materials.title': string;
  'studio.materials.empty': string;
  'studio.materials.add': string;
  'studio.materials.groupUnfold': string;
  'studio.materials.groupFold': string;
  'studio.bestiary.count': string;
  'studio.materials.addTo': string;
  'studio.materials.search': string;
  'studio.materials.inBeat': string;
  'studio.materials.isPrepSource': string;
  'studio.materials.nothingFound': string;
  'studio.materials.open': string;
  'studio.materials.remove': string;
  'studio.materials.adding': string;
  'studio.materials.removing': string;
  'studio.materials.offline': string;
  'studio.materials.addFailed': string;
  'studio.materials.removeFailed': string;
  'studio.materials.retry': string;
  'studio.materials.dismiss': string;
  'studio.materials.unavailable': string;
  'studio.materials.create': string;
  'studio.materials.chooseType': string;
  'studio.materials.creating': string;
  'studio.materials.createUnknown': string;
  'studio.materials.createUnresolved': string;
  'studio.materials.close': string;
  'studio.materials.sheetPc': string;
  'studio.materials.sheetNpc': string;
  'studio.materials.noOwner': string;
  'studio.materials.map': string;
  'studio.maps.title': string,
  'studio.maps.empty': string,
  'studio.maps.primary': string,
  'studio.maps.playersSee': string,
  'studio.maps.deleted': string,
  'studio.maps.openPrimary': string,
  'studio.maps.primaryDeleted': string,
  'studio.maps.noPrimary': string,
  'studio.maps.open': string,
  'studio.maps.beside': string,
  'studio.maps.show': string,
  'studio.maps.showing': string,
  'studio.maps.makePrimary': string,
  'studio.maps.unsetPrimary': string,
  'studio.maps.up': string,
  'studio.maps.down': string,
  'studio.maps.remove': string,
  'studio.maps.addPlaceholder': string,
  'studio.maps.add': string,
  'studio.maps.addAsPrimary': string,
  'studio.maps.allIn': string,
  'studio.maps.create': string,
  'studio.maps.createPrompt': string,
  'studio.maps.createPick': string;
  'studio.maps.createChange': string;
  'studio.maps.createNoImage': string;
  'studio.maps.createSubmit': string;
  'studio.maps.creating': string,
  'studio.maps.createFailed': string,
  'studio.maps.createUnknown': string,
  'studio.maps.createUnresolved': string,
  'studio.mapImage.upload': string,
  'studio.mapImage.replace': string,
  'studio.mapImage.uploading': string,
  'studio.mapImage.saving': string,
  'studio.mapImage.offline': string,
  'studio.mapImage.failed': string,
  'studio.mapImage.uploadFailed': string,
  'studio.maps.adding': string,
  'studio.maps.removing': string,
  'studio.maps.primaryChanging': string,
  'studio.maps.moving': string,
  'studio.maps.offline': string,
  'studio.maps.lost': string,
  'studio.maps.addFailed': string,
  'studio.maps.removeFailed': string,
  'studio.maps.primaryFailed': string,
  'studio.maps.moveFailed': string,
  'studio.maps.retry': string,
  'studio.maps.dismiss': string,
  'studio.maps.removePrimaryTitle': string,
  'studio.maps.removePrimaryBody': string,
  'studio.maps.keepNoPrimary': string,
  'studio.maps.makeNextPrimary': string,
  'studio.maps.removeConfirm': string,
  'studio.maps.cancel': string,
  'studio.beatPanel.prepUnknown': string;
  'studio.beatPanel.prepUnresolved': string;
  'studio.beatPanel.prepCreateNew': string;
  'studio.beatPanel.prepSaveFailed': string;
  'studio.beatPanel.prepResave': string;
  'studio.beatPanel.prepOffline': string;
  'studio.library.empty': string;
  'studio.library.noteLead': string;
  'studio.library.noteFine': string;
  'studio.rail.kindPc': string;
  'studio.rail.kindNpcSheet': string;
  'studio.rail.createSheetPc': string;
  'studio.rail.createSheetNpc': string;
  'studio.rail.mapsOnlyInBeats': string;
  'studio.palette.askAssistant': string;
  'studio.workingSet.add': string;
  'studio.rail.draftBadge': string;
  'studio.sheet.pendingDrafts': string;
  'studio.rail.sheetsPc': string;
  'studio.rail.sheetsNpc': string;
  'studio.rail.maps': string;
  'studio.rail.nested': string;
  'studio.rail.nestedBeat': string;
  'studio.rail.nestedQuest': string;
  'studio.owner.inBeat': string;
  'studio.owner.inObjective': string;
  'studio.owner.inQuest': string;
  'studio.owner.toShared': string;
  'studio.owner.mapToBeat': string;
  'studio.owner.move': string;
  'studio.owner.movePick': string;
  'studio.owner.moveBeatGroup': string;
  'studio.owner.moveObjectiveGroup': string;
  'studio.owner.objectiveOption': string;
  'studio.owner.noteInBeat': string;
  'studio.owner.noteToObjective': string;
  'studio.owner.beatNotes': string;
  'studio.owner.newNoteName': string;
  'studio.noteForm.name': string;
  'studio.noteForm.players': string;
  'studio.noteForm.gm': string;
  'studio.noteForm.done': string;
  'studio.noteForm.cancel': string;
  'studio.owner.failed': string;
  'studio.owner.secondUseTitle': string;
  'studio.owner.secondUseBody': string;
  'studio.owner.secondUseShare': string;
  'studio.owner.secondUseKeep': string;
  'studio.owner.secondUseCancel': string;
  'studio.note.playersSee': string;
  'studio.note.forGm': string;
  'studio.note.typeWord': string;
  'studio.note.placeOnMap': string;
  'studio.note.placeHint': string;
  'studio.note.onMapGm': string;
  'studio.note.onMapShown': string;
  'studio.note.pinOn': string;
  'studio.note.pinOffscreen': string;
  'studio.note.pinPlaced': string;
  'studio.note.makeShared': string;
  'studio.note.pinTitle': string;
  'studio.note.dragTitle': string;
  'studio.note.noMapForPlace': string;
  'studio.beat.mapBeside': string;
  'studio.beat.mapHide': string;
  'studio.mapbar.map': string;
  'studio.mapbar.thisBeat': string;
  'studio.mapbar.otherBeat': string;
  'studio.mapbar.dropHint': string;
  'studio.noteDrop.inBeat': string;
  'studio.noteDrop.inObjective': string;
  'studio.noteDrop.body': string;
  'studio.noteDrop.share': string;
  'studio.noteDrop.keep': string;
  'studio.noteDrop.cancel': string;
  'pinPopup.show': string;
  'pinPopup.shown': string;
  'pinPopup.tick': string;
  'pinPopup.objectiveDone': string;
  'pinPopup.hint': string;
  'pinPopup.confirmTitle': string;
  'pinPopup.confirmBody': string;
  'pinPopup.confirm': string;

  // === Studio Start Screen (T001) ===
  // Doors
  'studio.start.tell.title': string;
  'studio.start.tell.desc': string;
  'studio.start.load.title': string;
  'studio.start.load.desc': string;
  'studio.start.first.title': string;
  'studio.start.first.desc': string;
  // Bulk review — group labels
  'studio.start.bulk.group.npc': string;
  'studio.start.bulk.group.location': string;
  'studio.start.bulk.group.note': string;
  'studio.start.bulk.group.journal': string;
  'studio.start.bulk.group.encounter': string;
  // Bulk review — selected/total counter
  'studio.start.bulk.selected': string;
  // Bulk review — confidence buckets
  'studio.start.bulk.conf.high': string;
  'studio.start.bulk.conf.mid': string;
  'studio.start.bulk.conf.low': string;
  // Bulk review — action buttons
  'studio.start.bulk.apply': string;
  'studio.start.bulk.reject': string;
  // Maps banner (E5)
  'studio.start.maps.banner': string;
  'studio.start.maps.action': string;

  // === Studio Digest (T004) ===
  'studio.digest.button': string;
  'studio.digest.loading': string;
  'studio.digest.success': string;
  'studio.digest.empty': string;
  'studio.digest.error': string;
  'studio.sheet.ownerConfirm': string;
  'remarks.rail': string;
  'remarks.none': string;
  'remarks.heading': string;
  'remarks.configure': string;
  'remarks.hint': string;
  'remarks.pill.game': string;
  'remarks.pill.gameStale': string;
  'remarks.pill.opinion': string;
  'remarks.mark': string;
  'remarks.no': string;
  'remarks.discuss': string;
  'remarks.leave': string;
  'remarks.asIntended': string;
  'remarks.empty': string;
  'remarks.leftAsIs': string;
  'remarks.footer': string;
  'remarks.checkPrep': string;
  'remarks.checkPrepBusy': string;
  'remarks.checkPrepFailed': string;
  'remarks.badge.one': string;
  'remarks.badge.few': string;
  'remarks.badge.many': string;
  'remarks.settings.title': string;
  'remarks.sw.game': string;
  'remarks.sw.gameHint': string;
  'remarks.sw.prep': string;
  'remarks.sw.prepHint': string;
  'remarks.sw.fixes': string;
  'remarks.sw.fixesHint': string;
  'remarks.sw.simple': string;
  'remarks.sw.simpleHint': string;
  'remarks.sw.off': string;
  'remarks.fix.duplicate': string;
  'remarks.fix.fieldMisuse': string;
  'remarks.fix.emptyPrep': string;
  'remarks.fix.emptyNpc': string;
  'remarks.fix.brokenLink': string;
  'remarks.fix.model': string;
  'remarks.kind.quest': string;
  'remarks.kind.record': string;
  'remarks.t.duplicate': string;
  'remarks.t.emptyPrep': string;
  'remarks.t.emptyNpc': string;
  'remarks.t.fieldMisuse': string;
  'remarks.t.brokenLink': string;
  'remarks.b.duplicate': string;
  'remarks.b.emptyPrep': string;
  'remarks.b.emptyNpc': string;
  'remarks.b.fieldMisuse': string;
  'remarks.b.brokenLink': string;
  'remarks.game.body': string;
  'remarks.confirm.delete': string;
  'remarks.confirm.deleteBtn': string;
  'remarks.ask.prep': string;
  'remarks.ask.npc': string;
  'remarks.discussMsg': string;
  'remarks.hint.line': string;
  'remarks.hint.marked': string;
  'remarks.summary.line': string;
  'remarks.lk.beat': string;
  'remarks.lk.quest': string;
  'remarks.lk.record': string;
  'remarks.lk.sheet': string;
  'remarks.lk.npc': string;
  'remarks.lk.page': string;
  'search.open': string;
  'search.placeholder.gm': string;
  'search.placeholder.player': string;
  'search.empty': string;
  'search.emptyScope.gm': string;
  'search.emptyScope.player': string;
  'search.loadingSrd': string;
  'search.keys': string;
  'search.group.recent': string;
  'search.group.action': string;
  'search.group.beat': string;
  'search.group.journal': string;
  'search.group.page': string;
  'search.group.sheet': string;
  'search.group.token': string;
  'search.group.timeline': string;
  'search.group.monster': string;
  'search.group.spell': string;
  'search.group.condition': string;
  'search.group.item': string;
  'search.act.toMap': string;
  'search.act.toBeat': string;
  'search.act.beside': string;
  'search.act.openHere': string;
  'search.act.show': string;
  'search.act.reveal': string;
  'search.hint.current': string;
  'search.hint.open': string;
  'search.hint.players': string;
  'search.hint.cantrip': string;
  'search.hint.level': string;
  'search.cmd.toPrep': string;
  'search.cmd.toPlay': string;
  'search.cmd.openJournal': string;
  'search.cmd.openMaps': string;
  'search.cmd.openChronicle': string;
  'search.cmd.openRules': string;
  'search.cmd.openPlan': string;
  'search.srd.noMap': string;
  'search.srd.close': string;
  'search.srd.loading': string;
  'search.srd.failed': string;
  'search.maps.find': string;
  'search.hint.npc': string;
  'images.title': string;
  'images.titleBusy': string;
  'images.filter.all': string;
  'images.filter.map': string;
  'images.filter.portrait': string;
  'images.filter.chat': string;
  'images.filter.other': string;
  'images.what.map': string;
  'images.what.portrait': string;
  'images.what.chat': string;
  'images.what.token': string;
  'images.what.other': string;
  'images.empty': string;
  'images.emptyFilter': string;
  'images.generating': string;
  'images.failed': string;
  'images.failedShort': string;
  'images.offline': string;
  'images.retry': string;
  'images.open': string;
  'images.copyPrompt': string;
  'images.copied': string;
  'images.variant': string;
  'images.more': string;
  'images.delete': string;
  'images.deleteTitle': string;
  'images.deleteBody': string;
  'images.deleteChatBody': string;
  'images.deleteConfirm': string;
  'images.inUse': string;
  'images.back': string;
  'images.field.kind': string;
  'images.field.prompt': string;
  'images.field.userPrompt': string;
  'images.field.described': string;
  'images.describedByAi': string;
  'images.field.style': string;
  'images.field.model': string;
  'images.field.size': string;
  'images.field.author': string;
  'images.field.time': string;
  'images.field.usedIn': string;
  'images.field.error': string;
  'images.usedIn.page': string;
  'images.usedIn.sheet': string;
  'images.usedIn.token': string;
  'images.usedIn.journal': string;
  'images.usedIn.chat-message': string;
  'images.notUsed': string;
  'images.toast.ready': string;
  'images.toast.failed': string;
  'images.toast.portraitNotSet': string;
  'images.setPortrait': string;
  'images.use': string;
  'images.use.portrait': string;
  'images.use.token': string;
  'images.use.map': string;
  'images.use.hint.noMap': string;
  'images.use.kind.sheet': string;
  'images.use.kind.token': string;
  'images.use.kind.page': string;
  'images.use.hint.players': string;
  'images.use.hint.current': string;
  'images.use.hint.noImage': string;
  'images.use.hint.noPortrait': string;
  'images.use.hint.npc': string;
  'images.use.hint.pc': string;
  'images.use.scope': string;
  'images.use.placeholder': string;
  'images.use.suggested': string;
  'images.use.done': string;
  'images.use.mapTitle': string;
  'images.use.mapBody': string;
  'images.use.mapConfirm': string;
  'images.chatDeleted': string;
  'portraitGen.open': string;
  'portraitGen.fromSheet': string;
  'portraitGen.extra': string;
  'portraitGen.extraPlaceholder': string;
  'portraitGen.style': string;
  'portraitGen.style.fantasy painting': string;
  'portraitGen.style.ink sketch': string;
  'portraitGen.style.watercolor': string;
  'portraitGen.style.anime': string;
  'portraitGen.style.photorealistic': string;
  'portraitGen.create': string;
  'portraitGen.hint': string;
  'portraitGen.describe': string;
  'portraitGen.describeAgain': string;
  'portraitGen.describing': string;
  'portraitGen.description': string;
  'portraitGen.resetToSheet': string;
  'portraitGen.describedHint': string;
  'aiMap.background': string;
  'images.before': string;
  'images.after': string;
  'images.reject': string;
  'images.accept': string;
  'images.compare': string;
  'images.toast.compare': string;
  'images.toast.notPlaced': string;
  'images.placeMapWarn': string;
  'images.placeMapWarnDrawings': string;
  'images.placePortraitWarn': string;
  'images.inUseHidden': string;
  'images.sketchLost': string;
  'images.usedIn.note': string;
  'images.usedIn.alert': string;
  'images.usedIn.beat': string;
  'images.usedIn.timeline': string;
  'images.edit.inpaint': string;
  'images.edit.outpaint': string;
  'images.edit.detail': string;
  'images.edit.acceptDetail': string;
  'images.edit.replaceLinked': string;
  'images.edit.started': string;
  'sb.more': string;
  'sb.cancel': string;
  'sb.expand': string;
  'sb.collapse': string;
  'sb.collapseAll': string;
  'sb.showMore': string;
  'sb.showLess': string;
  'sb.nothingFound': string;
  'sb.filter': string;
  'sb.chip.all': string;
  'sb.gmOnly': string;
  'sb.playersSee': string;
  'sb.hidden': string;
  'sb.plan.openBeatInPrep': string;
  'sb.plan.openQuestInPrep': string;
  'sb.plan.current': string;
  'sb.note.open': string;
  'sb.note.reveal': string;
  'sb.note.hide': string;
  'sb.removeFromBeat': string;
  'sb.removedFromBeat': string;
  'sb.undo': string;
  'sb.delete': string;
  'sb.deleteYes': string;
  'sb.note.deleteTitle': string;
  'sb.note.deleteBody': string;
  'sb.sheet.open': string;
  'sb.sheet.npcKind': string;
  'sb.plan.list': string;
  'sb.map.youHere': string;
  'sb.maps.others': string;
  'sb.pins': string;
  'sb.objects': string;
  'sb.map.openTip': string;
  'sb.map.find': string;
  'sb.map.addToken': string;
  'sb.map.addPin': string;
  'sb.map.addObject': string;
  'sb.map.noObjects': string;
  'sb.map.noTokens': string;
  'sb.map.youAre': string;
  'sb.map.playerHint': string;
  'sb.map.playerTitle': string;
  'sb.tok.find': string;
  'sb.tok.hide': string;
  'sb.tok.show': string;
  'sb.tok.deleteTitle': string;
  'sb.tok.deleteBody': string;
  'sb.tok.mine': string;
  'sb.tok.presets': string;
  'sb.pin.open': string;
  'sb.pin.edit': string;
  'sb.pin.deleteTitle': string;
  'sb.pin.deleteBody': string;
  'sb.obj.edit': string;
  'sb.rail.title': string;
  'sb.rail.create': string;
  'sb.rail.createBeat': string;
  'sb.rail.createEntry': string;
  'sb.rail.createMap': string;
  'sb.rail.createPc': string;
  'sb.rail.createNpc': string;
  'sb.rail.collapseAll': string;
  'sb.rail.addBeat': string;
  'sb.rail.rename': string;
  'sb.rail.up': string;
  'sb.rail.down': string;
  'sb.rail.pin': string;
  'sb.rail.sheets': string;
  'sb.rail.newMap': string;
  'sb.rail.onlyInBeats': string;
  'sb.rail.beatN': string;
  'sb.rail.openRemarks': string;
  'sb.tl.world': string;
  'sb.tl.whatWas': string;
  'sb.tl.session': string;
  'sb.tl.noSession': string;
  'sb.tl.now': string;
  'sb.tl.stuckSession': string;
  'sb.tl.showSession': string;
  'sb.tl.earlier': string;
  'sb.tl.onlyCurrent': string;
  'sb.tl.desc': string;
  'sb.tl.gmOnly': string;
  'sb.tl.onlyPlayers': string;
  'sb.tl.edit': string;
  'sb.tl.showAll': string;
  'sb.tl.toGm': string;
  'sb.tl.toPlayers': string;
  'sb.tl.link': string;
  'sb.tl.deleteTitle': string;
  'sb.tl.deleteBody': string;
  'sb.tl.publish': string;
  'sb.tl.accept': string;
  'sb.tl.reject': string;
  'sb.tl.remove': string;
  'sb.tl.draft': string;
  'sb.tl.gmAuthor': string;
  'sb.tl.findGm': string;
  'sb.tl.find': string;
  'sb.tl.filters': string;
  'sb.tl.addToDay': string;
  'sb.tl.waitingGm': string;
  'sb.fx.own': string;
  'sb.fx.runAll': string;
  'sb.fx.edit': string;
  'sb.fx.auto': string;
  'sb.fx.dragHint': string;
  'sb.stickers': string;
  'sb.stickers.create': string;
  'sb.stickers.connect': string;
  'sb.srd.find': string;
  'sb.srd.open': string;
  'sb.srd.place': string;
  'sb.srd.placeWithSheet': string;
  'sb.hb.import': string;
  'sb.char.create': string;
  'sb.char.createBlank': string;
  'sb.char.pcs': string;
  'sb.char.npcs': string;
  'sb.char.mine': string;
  'sb.char.inBeats': string;
  'sb.char.inBeatsInfo': string;
  'sb.char.inFolder': string;
  'sb.char.owner': string;
  'sb.char.myOwn': string;
  'sb.char.show': string;
  'sb.char.hide': string;
  'sb.char.playersSee': string;
  'sb.char.hidden': string;
  'sb.char.card': string;
  'sb.char.manageNote': string;
  'sb.char.dragHint': string;
  'sb.journal.find': string;
  'sb.journal.create': string;
  'sb.journal.open': string;
  'sb.journal.edit': string;
  'sb.journal.reveal': string;
  'sb.journal.hide': string;
  'sb.journal.shownToggle': string;
  'sb.journal.hiddenToggle': string;
  'sb.journal.pin': string;
  'sb.journal.unpin': string;
  'sb.journal.pinned': string;
  'sb.journal.setSection': string;
  'sb.journal.sectionTitle': string;
  'sb.journal.sectionLabel': string;
  'sb.journal.sectionHint': string;
  'sb.journal.deleteTitle': string;
  'sb.journal.deleteBody': string;
  'sb.journal.playersSee': string;
  'sb.journal.personal': string;
  'sb.journal.playerEmpty': string;
  'sb.section.rename': string;
  'sb.section.renameTitle': string;
  'sb.section.remove': string;
  'sb.section.menu': string;
  'sb.nested': string;
  'sb.type.quest': string;
  'sb.type.npc': string;
  'sb.type.location': string;
  'sb.type.faction': string;
  'sb.type.handout': string;
  'sb.type.lore': string;
  'sb.quests.active': string;
  'sb.quests.completed': string;
  'sb.quests.failed': string;
  'sb.quests.hidden': string;
  'studio.sheet.ownerConfirmDesc': string;
  'studio.sheet.ownerConfirmTitle': string;
  'studio.bestiary.more': string;
  'studio.bestiary.add': string;
  'studio.bestiary.addTo': string;
  'studio.bestiary.noMap': string;
  'studio.bestiary.ruHint': string;
  'studio.materials.quests': string;
  'journal.chain.change': string;
  'characters.description': string;
  'journal.panel.nestedHint': string;
  'journal.panel.sharedHint': string;
  'journal.panel.playerHint': string;
  'studio.sheet.deleteGroupConfirm': string;
  'studio.sheet.renameGroupPrompt': string;
  'studio.sheet.deleteGroup': string;
  'studio.rail.groupMenu': string;
  'studio.rail.groupRename': string;
  'studio.rail.groupDelete': string;
  'journal.editor.group': string;
  'studio.sheet.renameGroup': string;
  'timeline.form.changedMeanwhile': string;
  'sidebar.map.playerHint': string;
  'timeline.cursor.nextDayPart': string;
  'timeline.cursor.worldHeading': string;
  'timeline.form.autoLinkedTime': string;
  'timeline.form.autoLinked': string;
  'timeline.form.atWorldTime': string;
  'studio.mode.play': string;
  'studio.mode.prep': string;
  'studio.mode.label': string;
  'studio.rail.bestiarySearch': string;
  'studio.rail.bestiary': string;
  'studio.quest.notInPlan': string;
  'studio.quest.wherePlan': string;
  'journal.quest.from': string;
  'journal.chain.next': string;
  'journal.chain.afterObjective': string;
  'journal.chain.followsFrom': string;
  'journal.form.quest.followsAnyObjective': string;
  'journal.form.quest.followsObjective': string;
  'journal.form.quest.followsFrom': string;
  'sidebar.map.heading': string;
  'studio.sheet.delete': string;
  'studio.sheet.publicFields': string;
  'studio.sheet.group': string;
  'studio.sheet.noOwner': string;
  'studio.sheet.owner': string;
  'studio.sheet.manage': string;
  'characters.manageHint': string;
  'characters.nested': string;

  // T101 i18n (SUPER MEGA 2026-05-31) — stat + skill labels
  'characterMode.stat.str': string;
  'characterMode.stat.dex': string;
  'characterMode.stat.con': string;
  'characterMode.stat.int': string;
  'characterMode.stat.wis': string;
  'characterMode.stat.cha': string;
  'characterMode.saveAbbr': string;
  'characterMode.saveProficiencyHint': string;
  'characterMode.skill.acrobatics': string;
  'characterMode.skill.animalHandling': string;
  'characterMode.skill.arcana': string;
  'characterMode.skill.athletics': string;
  'characterMode.skill.deception': string;
  'characterMode.skill.history': string;
  'characterMode.skill.insight': string;
  'characterMode.skill.intimidation': string;
  'characterMode.skill.investigation': string;
  'characterMode.skill.medicine': string;
  'characterMode.skill.nature': string;
  'characterMode.skill.perception': string;
  'characterMode.skill.performance': string;
  'characterMode.skill.persuasion': string;
  'characterMode.skill.religion': string;
  'characterMode.skill.sleightOfHand': string;
  'characterMode.skill.stealth': string;
  'characterMode.skill.survival': string;
}

/** I18n context value provided by the Provider */
export interface I18nContextValue {
  /** Current locale */
  locale: Locale;
  /** Translation function */
  t: (key: keyof TranslationKeys) => string;
  /** Change the active locale (persists to localStorage) */
  setLocale: (locale: Locale) => void;
}
